/**
 * fold-to-ascii.js
 * https://github.com/mplatt/fold-to-ascii-js
 *
 * This is a JavaScript port of the Apache Lucene ASCII Folding Filter.
 *
 * The Apache Lucene ASCII Folding Filter is licensed to the Apache Software
 * Foundation (ASF) under one or more contributor license agreements. See the
 * NOTICE file distributed with this work for additional information regarding
 * copyright ownership. The ASF licenses this file to You under the Apache
 * License, Version 2.0 (the "License"); you may not use this file except in
 * compliance with the License. You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS, WITHOUT
 * WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied. See the
 * License for the specific language governing permissions and limitations under
 * the License.
 *
 * This port uses an example from the Mozilla Developer Network published prior
 * to August 20, 2010
 *
 * fixedCharCodeAt is licencesed under the MIT License (MIT)
 *
 * Copyright (c) 2013 Mozilla Developer Network and individual contributors
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

/*
 * True if unmapped non-ASCII characters should be replaced by the
 * default-string.
 * False if unmapped characters should remain in the output string.
 */
const replaceUnmapped = true;

/*
 * Default string to replace unmapped characters with
 */
const defaultString = '_';

const fixedCharCodeAt = (str: string, idx?: number): number => {
    /*
     * ex. fixedCharCodeAt ('\uD800\uDC00', 0); // 65536
     * ex. fixedCharCodeAt ('\uD800\uDC00', 1); // 65536
     */
    idx = idx || 0;
    const code = str.charCodeAt(idx);
    let hi: number;
    let low: number;

    /*
     * High surrogate (could change last hex to 0xDB7F to treat high
     * private surrogates as single characters)
     */
    if (0xd800 <= code && code <= 0xdbff) {
        hi = code;
        low = str.charCodeAt(idx + 1);
        if (isNaN(low)) {
            throw 'High surrogate not followed by low surrogate in fixedCharCodeAt()';
        }
        // eslint-disable-next-line no-mixed-operators
        return (hi - 0xd800) * 0x400 + (low - 0xdc00 + 0x10000);
    }
    if (0xdc00 <= code && code <= 0xdfff) {
        /*
         * Low surrogate: We return 0 to allow loops to skip this
         * iteration since should have already handled high surrogate above
         * in the previous iteration
         */
        return 0;
        /*
         * hi = str.charCodeAt(idx-1); low = code; return ((hi - 0xD800) *
         * 0x400) + (low - 0xDC00) + 0x10000;
         */
    }
    return code;
};

/*
 * Replaces a character with an ASCII compliant character or
 * character-combination.
 */
const replaceChar = (charCode: number): string => {
    let outString = '';

    switch (charCode) {
        case 0xc0: // À	[LATIN CAPITAL LETTER A WITH GRAVE]
        case 0xc1: // Á	[LATIN CAPITAL LETTER A WITH ACUTE]
        case 0xc2: // Â	[LATIN CAPITAL LETTER A WITH CIRCUMFLEX]
        case 0xc3: // Ã	[LATIN CAPITAL LETTER A WITH TILDE]
        case 0xc4: // Ä	[LATIN CAPITAL LETTER A WITH DIAERESIS]
        case 0xc5: // Å	[LATIN CAPITAL LETTER A WITH RING ABOVE]
        case 0x100: // Ā	[LATIN CAPITAL LETTER A WITH MACRON]
        case 0x102: // Ă	[LATIN CAPITAL LETTER A WITH BREVE]
        case 0x104: // Ą	[LATIN CAPITAL LETTER A WITH OGONEK]
        case 0x18f: // Ə	http://en.wikipedia.org/wiki/Schwa	[LATIN CAPITAL LETTER SCHWA]
        case 0x1cd: // Ǎ	[LATIN CAPITAL LETTER A WITH CARON]
        case 0x1de: // Ǟ	[LATIN CAPITAL LETTER A WITH DIAERESIS AND MACRON]
        case 0x1e0: // Ǡ	[LATIN CAPITAL LETTER A WITH DOT ABOVE AND MACRON]
        case 0x1fa: // Ǻ	[LATIN CAPITAL LETTER A WITH RING ABOVE AND ACUTE]
        case 0x200: // Ȁ	[LATIN CAPITAL LETTER A WITH DOUBLE GRAVE]
        case 0x202: // Ȃ	[LATIN CAPITAL LETTER A WITH INVERTED BREVE]
        case 0x226: // Ȧ	[LATIN CAPITAL LETTER A WITH DOT ABOVE]
        case 0x23a: // Ⱥ	[LATIN CAPITAL LETTER A WITH STROKE]
        case 0x1d00: // ᴀ	[LATIN LETTER SMALL CAPITAL A]
        case 0x1e00: // Ḁ	[LATIN CAPITAL LETTER A WITH RING BELOW]
        case 0x1ea0: // Ạ	[LATIN CAPITAL LETTER A WITH DOT BELOW]
        case 0x1ea2: // Ả	[LATIN CAPITAL LETTER A WITH HOOK ABOVE]
        case 0x1ea4: // Ấ	[LATIN CAPITAL LETTER A WITH CIRCUMFLEX AND ACUTE]
        case 0x1ea6: // Ầ	[LATIN CAPITAL LETTER A WITH CIRCUMFLEX AND GRAVE]
        case 0x1ea8: // Ẩ	[LATIN CAPITAL LETTER A WITH CIRCUMFLEX AND HOOK ABOVE]
        case 0x1eaa: // Ẫ	[LATIN CAPITAL LETTER A WITH CIRCUMFLEX AND TILDE]
        case 0x1eac: // Ậ	[LATIN CAPITAL LETTER A WITH CIRCUMFLEX AND DOT BELOW]
        case 0x1eae: // Ắ	[LATIN CAPITAL LETTER A WITH BREVE AND ACUTE]
        case 0x1eb0: // Ằ	[LATIN CAPITAL LETTER A WITH BREVE AND GRAVE]
        case 0x1eb2: // Ẳ	[LATIN CAPITAL LETTER A WITH BREVE AND HOOK ABOVE]
        case 0x1eb4: // Ẵ	[LATIN CAPITAL LETTER A WITH BREVE AND TILDE]
        case 0x1eb6: // Ặ	[LATIN CAPITAL LETTER A WITH BREVE AND DOT BELOW]
        case 0x24b6: // Ⓐ	[CIRCLED LATIN CAPITAL LETTER A]
        case 0xff21: // Ａ	[FULLWIDTH LATIN CAPITAL LETTER A]
            outString += 'A';
            break;
        case 0xe0: // à	[LATIN SMALL LETTER A WITH GRAVE]
        case 0xe1: // á	[LATIN SMALL LETTER A WITH ACUTE]
        case 0xe2: // â	[LATIN SMALL LETTER A WITH CIRCUMFLEX]
        case 0xe3: // ã	[LATIN SMALL LETTER A WITH TILDE]
        case 0xe4: // ä	[LATIN SMALL LETTER A WITH DIAERESIS]
        case 0xe5: // å	[LATIN SMALL LETTER A WITH RING ABOVE]
        case 0x101: // ā	[LATIN SMALL LETTER A WITH MACRON]
        case 0x103: // ă	[LATIN SMALL LETTER A WITH BREVE]
        case 0x105: // ą	[LATIN SMALL LETTER A WITH OGONEK]
        case 0x1ce: // ǎ	[LATIN SMALL LETTER A WITH CARON]
        case 0x1df: // ǟ	[LATIN SMALL LETTER A WITH DIAERESIS AND MACRON]
        case 0x1e1: // ǡ	[LATIN SMALL LETTER A WITH DOT ABOVE AND MACRON]
        case 0x1fb: // ǻ	[LATIN SMALL LETTER A WITH RING ABOVE AND ACUTE]
        case 0x201: // ȁ	[LATIN SMALL LETTER A WITH DOUBLE GRAVE]
        case 0x203: // ȃ	[LATIN SMALL LETTER A WITH INVERTED BREVE]
        case 0x227: // ȧ	[LATIN SMALL LETTER A WITH DOT ABOVE]
        case 0x250: // ɐ	[LATIN SMALL LETTER TURNED A]
        case 0x259: // ə	[LATIN SMALL LETTER SCHWA]
        case 0x25a: // ɚ	[LATIN SMALL LETTER SCHWA WITH HOOK]
        case 0x1d8f: // ᶏ	[LATIN SMALL LETTER A WITH RETROFLEX HOOK]
        case 0x1d95: // ᶕ	[LATIN SMALL LETTER SCHWA WITH RETROFLEX HOOK]
        case 0x1e01: // ạ	[LATIN SMALL LETTER A WITH RING BELOW]
        case 0x1e9a: // ả	[LATIN SMALL LETTER A WITH RIGHT HALF RING]
        case 0x1ea1: // ạ	[LATIN SMALL LETTER A WITH DOT BELOW]
        case 0x1ea3: // ả	[LATIN SMALL LETTER A WITH HOOK ABOVE]
        case 0x1ea5: // ấ	[LATIN SMALL LETTER A WITH CIRCUMFLEX AND ACUTE]
        case 0x1ea7: // ầ	[LATIN SMALL LETTER A WITH CIRCUMFLEX AND GRAVE]
        case 0x1ea9: // ẩ	[LATIN SMALL LETTER A WITH CIRCUMFLEX AND HOOK ABOVE]
        case 0x1eab: // ẫ	[LATIN SMALL LETTER A WITH CIRCUMFLEX AND TILDE]
        case 0x1ead: // ậ	[LATIN SMALL LETTER A WITH CIRCUMFLEX AND DOT BELOW]
        case 0x1eaf: // ắ	[LATIN SMALL LETTER A WITH BREVE AND ACUTE]
        case 0x1eb1: // ằ	[LATIN SMALL LETTER A WITH BREVE AND GRAVE]
        case 0x1eb3: // ẳ	[LATIN SMALL LETTER A WITH BREVE AND HOOK ABOVE]
        case 0x1eb5: // ẵ	[LATIN SMALL LETTER A WITH BREVE AND TILDE]
        case 0x1eb7: // ặ	[LATIN SMALL LETTER A WITH BREVE AND DOT BELOW]
        case 0x2090: // ₐ	[LATIN SUBSCRIPT SMALL LETTER A]
        case 0x2094: // ₔ	[LATIN SUBSCRIPT SMALL LETTER SCHWA]
        case 0x24d0: // ⓐ	[CIRCLED LATIN SMALL LETTER A]
        case 0x2c65: // ⱥ	[LATIN SMALL LETTER A WITH STROKE]
        case 0x2c6f: // Ɐ	[LATIN CAPITAL LETTER TURNED A]
        case 0xff41: // ａ	[FULLWIDTH LATIN SMALL LETTER A]
            outString += 'a';
            break;
        case 0xa732: // Ꜳ	[LATIN CAPITAL LETTER AA]
            outString += 'A';
            outString += 'A';
            break;
        case 0xc6: // Æ	[LATIN CAPITAL LETTER AE]
        case 0x1e2: // Ǣ	[LATIN CAPITAL LETTER AE WITH MACRON]
        case 0x1fc: // Ǽ	[LATIN CAPITAL LETTER AE WITH ACUTE]
        case 0x1d01: // ᴁ	[LATIN LETTER SMALL CAPITAL AE]
            outString += 'A';
            outString += 'E';
            break;
        case 0xa734: // Ꜵ	[LATIN CAPITAL LETTER AO]
            outString += 'A';
            outString += 'O';
            break;
        case 0xa736: // Ꜷ	[LATIN CAPITAL LETTER AU]
            outString += 'A';
            outString += 'U';
            break;
        case 0xa738: // Ꜹ	[LATIN CAPITAL LETTER AV]
        case 0xa73a: // Ꜻ	[LATIN CAPITAL LETTER AV WITH HORIZONTAL BAR]
            outString += 'A';
            outString += 'V';
            break;
        case 0xa73c: // Ꜽ	[LATIN CAPITAL LETTER AY]
            outString += 'A';
            outString += 'Y';
            break;
        case 0x249c: // ⒜	[PARENTHESIZED LATIN SMALL LETTER A]
            outString += '(';
            outString += 'a';
            outString += ')';
            break;
        case 0xa733: // ꜳ	[LATIN SMALL LETTER AA]
            outString += 'a';
            outString += 'a';
            break;
        case 0xe6: // æ	[LATIN SMALL LETTER AE]
        case 0x1e3: // ǣ	[LATIN SMALL LETTER AE WITH MACRON]
        case 0x1fd: // ǽ	[LATIN SMALL LETTER AE WITH ACUTE]
        case 0x1d02: // ᴂ	[LATIN SMALL LETTER TURNED AE]
            outString += 'a';
            outString += 'e';
            break;
        case 0xa735: // ꜵ	[LATIN SMALL LETTER AO]
            outString += 'a';
            outString += 'o';
            break;
        case 0xa737: // ꜷ	[LATIN SMALL LETTER AU]
            outString += 'a';
            outString += 'u';
            break;
        case 0xa739: // ꜹ	[LATIN SMALL LETTER AV]
        case 0xa73b: // ꜻ	[LATIN SMALL LETTER AV WITH HORIZONTAL BAR]
            outString += 'a';
            outString += 'v';
            break;
        case 0xa73d: // ꜽ	[LATIN SMALL LETTER AY]
            outString += 'a';
            outString += 'y';
            break;
        case 0x181: // Ɓ	[LATIN CAPITAL LETTER B WITH HOOK]
        case 0x182: // Ƃ	[LATIN CAPITAL LETTER B WITH TOPBAR]
        case 0x243: // Ƀ	[LATIN CAPITAL LETTER B WITH STROKE]
        case 0x299: // ʙ	[LATIN LETTER SMALL CAPITAL B]
        case 0x1d03: // ᴃ	[LATIN LETTER SMALL CAPITAL BARRED B]
        case 0x1e02: // Ḃ	[LATIN CAPITAL LETTER B WITH DOT ABOVE]
        case 0x1e04: // Ḅ	[LATIN CAPITAL LETTER B WITH DOT BELOW]
        case 0x1e06: // Ḇ	[LATIN CAPITAL LETTER B WITH LINE BELOW]
        case 0x24b7: // Ⓑ	[CIRCLED LATIN CAPITAL LETTER B]
        case 0xff22: // Ｂ	[FULLWIDTH LATIN CAPITAL LETTER B]
            outString += 'B';
            break;
        case 0x180: // ƀ	[LATIN SMALL LETTER B WITH STROKE]
        case 0x183: // ƃ	[LATIN SMALL LETTER B WITH TOPBAR]
        case 0x253: // ɓ	[LATIN SMALL LETTER B WITH HOOK]
        case 0x1d6c: // ᵬ	[LATIN SMALL LETTER B WITH MIDDLE TILDE]
        case 0x1d80: // ᶀ	[LATIN SMALL LETTER B WITH PALATAL HOOK]
        case 0x1e03: // ḃ	[LATIN SMALL LETTER B WITH DOT ABOVE]
        case 0x1e05: // ḅ	[LATIN SMALL LETTER B WITH DOT BELOW]
        case 0x1e07: // ḇ	[LATIN SMALL LETTER B WITH LINE BELOW]
        case 0x24d1: // ⓑ	[CIRCLED LATIN SMALL LETTER B]
        case 0xff42: // ｂ	[FULLWIDTH LATIN SMALL LETTER B]
            outString += 'b';
            break;
        case 0x249d: // ⒝	[PARENTHESIZED LATIN SMALL LETTER B]
            outString += '(';
            outString += 'b';
            outString += ')';
            break;
        case 0xc7: // Ç	[LATIN CAPITAL LETTER C WITH CEDILLA]
        case 0x106: // Ć	[LATIN CAPITAL LETTER C WITH ACUTE]
        case 0x108: // Ĉ	[LATIN CAPITAL LETTER C WITH CIRCUMFLEX]
        case 0x10a: // Ċ	[LATIN CAPITAL LETTER C WITH DOT ABOVE]
        case 0x10c: // Č	[LATIN CAPITAL LETTER C WITH CARON]
        case 0x187: // Ƈ	[LATIN CAPITAL LETTER C WITH HOOK]
        case 0x23b: // Ȼ	[LATIN CAPITAL LETTER C WITH STROKE]
        case 0x297: // ʗ	[LATIN LETTER STRETCHED C]
        case 0x1d04: // ᴄ	[LATIN LETTER SMALL CAPITAL C]
        case 0x1e08: // Ḉ	[LATIN CAPITAL LETTER C WITH CEDILLA AND ACUTE]
        case 0x24b8: // Ⓒ	[CIRCLED LATIN CAPITAL LETTER C]
        case 0xff23: // Ｃ	[FULLWIDTH LATIN CAPITAL LETTER C]
            outString += 'C';
            break;
        case 0xe7: // ç	[LATIN SMALL LETTER C WITH CEDILLA]
        case 0x107: // ć	[LATIN SMALL LETTER C WITH ACUTE]
        case 0x109: // ĉ	[LATIN SMALL LETTER C WITH CIRCUMFLEX]
        case 0x10b: // ċ	[LATIN SMALL LETTER C WITH DOT ABOVE]
        case 0x10d: // č	[LATIN SMALL LETTER C WITH CARON]
        case 0x188: // ƈ	[LATIN SMALL LETTER C WITH HOOK]
        case 0x23c: // ȼ	[LATIN SMALL LETTER C WITH STROKE]
        case 0x255: // ɕ	[LATIN SMALL LETTER C WITH CURL]
        case 0x1e09: // ḉ	[LATIN SMALL LETTER C WITH CEDILLA AND ACUTE]
        case 0x2184: // ↄ	[LATIN SMALL LETTER REVERSED C]
        case 0x24d2: // ⓒ	[CIRCLED LATIN SMALL LETTER C]
        case 0xa73e: // Ꜿ	[LATIN CAPITAL LETTER REVERSED C WITH DOT]
        case 0xa73f: // ꜿ	[LATIN SMALL LETTER REVERSED C WITH DOT]
        case 0xff43: // ｃ	[FULLWIDTH LATIN SMALL LETTER C]
            outString += 'c';
            break;
        case 0x249e: // ⒞	[PARENTHESIZED LATIN SMALL LETTER C]
            outString += '(';
            outString += 'c';
            outString += ')';
            break;
        case 0xd0: // Ð	[LATIN CAPITAL LETTER ETH]
        case 0x10e: // Ď	[LATIN CAPITAL LETTER D WITH CARON]
        case 0x110: // Đ	[LATIN CAPITAL LETTER D WITH STROKE]
        case 0x189: // Ɖ	[LATIN CAPITAL LETTER AFRICAN D]
        case 0x18a: // Ɗ	[LATIN CAPITAL LETTER D WITH HOOK]
        case 0x18b: // Ƌ	[LATIN CAPITAL LETTER D WITH TOPBAR]
        case 0x1d05: // ᴅ	[LATIN LETTER SMALL CAPITAL D]
        case 0x1d06: // ᴆ	[LATIN LETTER SMALL CAPITAL ETH]
        case 0x1e0a: // Ḋ	[LATIN CAPITAL LETTER D WITH DOT ABOVE]
        case 0x1e0c: // Ḍ	[LATIN CAPITAL LETTER D WITH DOT BELOW]
        case 0x1e0e: // Ḏ	[LATIN CAPITAL LETTER D WITH LINE BELOW]
        case 0x1e10: // Ḑ	[LATIN CAPITAL LETTER D WITH CEDILLA]
        case 0x1e12: // Ḓ	[LATIN CAPITAL LETTER D WITH CIRCUMFLEX BELOW]
        case 0x24b9: // Ⓓ	[CIRCLED LATIN CAPITAL LETTER D]
        case 0xa779: // Ꝺ	[LATIN CAPITAL LETTER INSULAR D]
        case 0xff24: // Ｄ	[FULLWIDTH LATIN CAPITAL LETTER D]
            outString += 'D';
            break;
        case 0xf0: // ð	[LATIN SMALL LETTER ETH]
        case 0x10f: // ď	[LATIN SMALL LETTER D WITH CARON]
        case 0x111: // đ	[LATIN SMALL LETTER D WITH STROKE]
        case 0x18c: // ƌ	[LATIN SMALL LETTER D WITH TOPBAR]
        case 0x221: // ȡ	[LATIN SMALL LETTER D WITH CURL]
        case 0x256: // ɖ	[LATIN SMALL LETTER D WITH TAIL]
        case 0x257: // ɗ	[LATIN SMALL LETTER D WITH HOOK]
        case 0x1d6d: // ᵭ	[LATIN SMALL LETTER D WITH MIDDLE TILDE]
        case 0x1d81: // ᶁ	[LATIN SMALL LETTER D WITH PALATAL HOOK]
        case 0x1d91: // ᶑ	[LATIN SMALL LETTER D WITH HOOK AND TAIL]
        case 0x1e0b: // ḋ	[LATIN SMALL LETTER D WITH DOT ABOVE]
        case 0x1e0d: // ḍ	[LATIN SMALL LETTER D WITH DOT BELOW]
        case 0x1e0f: // ḏ	[LATIN SMALL LETTER D WITH LINE BELOW]
        case 0x1e11: // ḑ	[LATIN SMALL LETTER D WITH CEDILLA]
        case 0x1e13: // ḓ	[LATIN SMALL LETTER D WITH CIRCUMFLEX BELOW]
        case 0x24d3: // ⓓ	[CIRCLED LATIN SMALL LETTER D]
        case 0xa77a: // ꝺ	[LATIN SMALL LETTER INSULAR D]
        case 0xff44: // ｄ	[FULLWIDTH LATIN SMALL LETTER D]
            outString += 'd';
            break;
        case 0x1c4: // Ǆ	[LATIN CAPITAL LETTER DZ WITH CARON]
        case 0x1f1: // Ǳ	[LATIN CAPITAL LETTER DZ]
            outString += 'D';
            outString += 'Z';
            break;
        case 0x1c5: // ǅ	[LATIN CAPITAL LETTER D WITH SMALL LETTER Z WITH CARON]
        case 0x1f2: // ǲ	[LATIN CAPITAL LETTER D WITH SMALL LETTER Z]
            outString += 'D';
            outString += 'z';
            break;
        case 0x249f: // ⒟	[PARENTHESIZED LATIN SMALL LETTER D]
            outString += '(';
            outString += 'd';
            outString += ')';
            break;
        case 0x238: // ȸ	[LATIN SMALL LETTER DB DIGRAPH]
            outString += 'd';
            outString += 'b';
            break;
        case 0x1c6: // ǆ	[LATIN SMALL LETTER DZ WITH CARON]
        case 0x1f3: // ǳ	[LATIN SMALL LETTER DZ]
        case 0x2a3: // ʣ	[LATIN SMALL LETTER DZ DIGRAPH]
        case 0x2a5: // ʥ	[LATIN SMALL LETTER DZ DIGRAPH WITH CURL]
            outString += 'd';
            outString += 'z';
            break;
        case 0xc8: // È	[LATIN CAPITAL LETTER E WITH GRAVE]
        case 0xc9: // É	[LATIN CAPITAL LETTER E WITH ACUTE]
        case 0xca: // Ê	[LATIN CAPITAL LETTER E WITH CIRCUMFLEX]
        case 0xcb: // Ë	[LATIN CAPITAL LETTER E WITH DIAERESIS]
        case 0x112: // Ē	[LATIN CAPITAL LETTER E WITH MACRON]
        case 0x114: // Ĕ	[LATIN CAPITAL LETTER E WITH BREVE]
        case 0x116: // Ė	[LATIN CAPITAL LETTER E WITH DOT ABOVE]
        case 0x118: // Ę	[LATIN CAPITAL LETTER E WITH OGONEK]
        case 0x11a: // Ě	[LATIN CAPITAL LETTER E WITH CARON]
        case 0x18e: // Ǝ	[LATIN CAPITAL LETTER REVERSED E]
        case 0x190: // Ɛ	[LATIN CAPITAL LETTER OPEN E]
        case 0x204: // Ȅ	[LATIN CAPITAL LETTER E WITH DOUBLE GRAVE]
        case 0x206: // Ȇ	[LATIN CAPITAL LETTER E WITH INVERTED BREVE]
        case 0x228: // Ȩ	[LATIN CAPITAL LETTER E WITH CEDILLA]
        case 0x246: // Ɇ	[LATIN CAPITAL LETTER E WITH STROKE]
        case 0x1d07: // ᴇ	[LATIN LETTER SMALL CAPITAL E]
        case 0x1e14: // Ḕ	[LATIN CAPITAL LETTER E WITH MACRON AND GRAVE]
        case 0x1e16: // Ḗ	[LATIN CAPITAL LETTER E WITH MACRON AND ACUTE]
        case 0x1e18: // Ḙ	[LATIN CAPITAL LETTER E WITH CIRCUMFLEX BELOW]
        case 0x1e1a: // Ḛ	[LATIN CAPITAL LETTER E WITH TILDE BELOW]
        case 0x1e1c: // Ḝ	[LATIN CAPITAL LETTER E WITH CEDILLA AND BREVE]
        case 0x1eb8: // Ẹ	[LATIN CAPITAL LETTER E WITH DOT BELOW]
        case 0x1eba: // Ẻ	[LATIN CAPITAL LETTER E WITH HOOK ABOVE]
        case 0x1ebc: // Ẽ	[LATIN CAPITAL LETTER E WITH TILDE]
        case 0x1ebe: // Ế	[LATIN CAPITAL LETTER E WITH CIRCUMFLEX AND ACUTE]
        case 0x1ec0: // Ề	[LATIN CAPITAL LETTER E WITH CIRCUMFLEX AND GRAVE]
        case 0x1ec2: // Ể	[LATIN CAPITAL LETTER E WITH CIRCUMFLEX AND HOOK ABOVE]
        case 0x1ec4: // Ễ	[LATIN CAPITAL LETTER E WITH CIRCUMFLEX AND TILDE]
        case 0x1ec6: // Ệ	[LATIN CAPITAL LETTER E WITH CIRCUMFLEX AND DOT BELOW]
        case 0x24ba: // Ⓔ	[CIRCLED LATIN CAPITAL LETTER E]
        case 0x2c7b: // ⱻ	[LATIN LETTER SMALL CAPITAL TURNED E]
        case 0xff25: // Ｅ	[FULLWIDTH LATIN CAPITAL LETTER E]
            outString += 'E';
            break;
        case 0xe8: // è	[LATIN SMALL LETTER E WITH GRAVE]
        case 0xe9: // é	[LATIN SMALL LETTER E WITH ACUTE]
        case 0xea: // ê	[LATIN SMALL LETTER E WITH CIRCUMFLEX]
        case 0xeb: // ë	[LATIN SMALL LETTER E WITH DIAERESIS]
        case 0x113: // ē	[LATIN SMALL LETTER E WITH MACRON]
        case 0x115: // ĕ	[LATIN SMALL LETTER E WITH BREVE]
        case 0x117: // ė	[LATIN SMALL LETTER E WITH DOT ABOVE]
        case 0x119: // ę	[LATIN SMALL LETTER E WITH OGONEK]
        case 0x11b: // ě	[LATIN SMALL LETTER E WITH CARON]
        case 0x1dd: // ǝ	[LATIN SMALL LETTER TURNED E]
        case 0x205: // ȅ	[LATIN SMALL LETTER E WITH DOUBLE GRAVE]
        case 0x207: // ȇ	[LATIN SMALL LETTER E WITH INVERTED BREVE]
        case 0x229: // ȩ	[LATIN SMALL LETTER E WITH CEDILLA]
        case 0x247: // ɇ	[LATIN SMALL LETTER E WITH STROKE]
        case 0x258: // ɘ	[LATIN SMALL LETTER REVERSED E]
        case 0x25b: // ɛ	[LATIN SMALL LETTER OPEN E]
        case 0x25c: // ɜ	[LATIN SMALL LETTER REVERSED OPEN E]
        case 0x25d: // ɝ	[LATIN SMALL LETTER REVERSED OPEN E WITH HOOK]
        case 0x25e: // ɞ	[LATIN SMALL LETTER CLOSED REVERSED OPEN E]
        case 0x29a: // ʚ	[LATIN SMALL LETTER CLOSED OPEN E]
        case 0x1d08: // ᴈ	[LATIN SMALL LETTER TURNED OPEN E]
        case 0x1d92: // ᶒ	[LATIN SMALL LETTER E WITH RETROFLEX HOOK]
        case 0x1d93: // ᶓ	[LATIN SMALL LETTER OPEN E WITH RETROFLEX HOOK]
        case 0x1d94: // ᶔ	[LATIN SMALL LETTER REVERSED OPEN E WITH RETROFLEX HOOK]
        case 0x1e15: // ḕ	[LATIN SMALL LETTER E WITH MACRON AND GRAVE]
        case 0x1e17: // ḗ	[LATIN SMALL LETTER E WITH MACRON AND ACUTE]
        case 0x1e19: // ḙ	[LATIN SMALL LETTER E WITH CIRCUMFLEX BELOW]
        case 0x1e1b: // ḛ	[LATIN SMALL LETTER E WITH TILDE BELOW]
        case 0x1e1d: // ḝ	[LATIN SMALL LETTER E WITH CEDILLA AND BREVE]
        case 0x1eb9: // ẹ	[LATIN SMALL LETTER E WITH DOT BELOW]
        case 0x1ebb: // ẻ	[LATIN SMALL LETTER E WITH HOOK ABOVE]
        case 0x1ebd: // ẽ	[LATIN SMALL LETTER E WITH TILDE]
        case 0x1ebf: // ế	[LATIN SMALL LETTER E WITH CIRCUMFLEX AND ACUTE]
        case 0x1ec1: // ề	[LATIN SMALL LETTER E WITH CIRCUMFLEX AND GRAVE]
        case 0x1ec3: // ể	[LATIN SMALL LETTER E WITH CIRCUMFLEX AND HOOK ABOVE]
        case 0x1ec5: // ễ	[LATIN SMALL LETTER E WITH CIRCUMFLEX AND TILDE]
        case 0x1ec7: // ệ	[LATIN SMALL LETTER E WITH CIRCUMFLEX AND DOT BELOW]
        case 0x2091: // ₑ	[LATIN SUBSCRIPT SMALL LETTER E]
        case 0x24d4: // ⓔ	[CIRCLED LATIN SMALL LETTER E]
        case 0x2c78: // ⱸ	[LATIN SMALL LETTER E WITH NOTCH]
        case 0xff45: // ｅ	[FULLWIDTH LATIN SMALL LETTER E]
            outString += 'e';
            break;
        case 0x24a0: // ⒠	[PARENTHESIZED LATIN SMALL LETTER E]
            outString += '(';
            outString += 'e';
            outString += ')';
            break;
        case 0x191: // Ƒ	[LATIN CAPITAL LETTER F WITH HOOK]
        case 0x1e1e: // Ḟ	[LATIN CAPITAL LETTER F WITH DOT ABOVE]
        case 0x24bb: // Ⓕ	[CIRCLED LATIN CAPITAL LETTER F]
        case 0xa730: // ꜰ	[LATIN LETTER SMALL CAPITAL F]
        case 0xa77b: // Ꝼ	[LATIN CAPITAL LETTER INSULAR F]
        case 0xa7fb: // ꟻ	[LATIN EPIGRAPHIC LETTER REVERSED F]
        case 0xff26: // Ｆ	[FULLWIDTH LATIN CAPITAL LETTER F]
            outString += 'F';
            break;
        case 0x192: // ƒ	[LATIN SMALL LETTER F WITH HOOK]
        case 0x1d6e: // ᵮ	[LATIN SMALL LETTER F WITH MIDDLE TILDE]
        case 0x1d82: // ᶂ	[LATIN SMALL LETTER F WITH PALATAL HOOK]
        case 0x1e1f: // ḟ	[LATIN SMALL LETTER F WITH DOT ABOVE]
        case 0x1e9b: // ẛ	[LATIN SMALL LETTER LONG S WITH DOT ABOVE]
        case 0x24d5: // ⓕ	[CIRCLED LATIN SMALL LETTER F]
        case 0xa77c: // ꝼ	[LATIN SMALL LETTER INSULAR F]
        case 0xff46: // ｆ	[FULLWIDTH LATIN SMALL LETTER F]
            outString += 'f';
            break;
        case 0x24a1: // ⒡	[PARENTHESIZED LATIN SMALL LETTER F]
            outString += '(';
            outString += 'f';
            outString += ')';
            break;
        case 0xfb00: // ﬀ	[LATIN SMALL LIGATURE FF]
            outString += 'f';
            outString += 'f';
            break;
        case 0xfb03: // ﬃ	[LATIN SMALL LIGATURE FFI]
            outString += 'f';
            outString += 'f';
            outString += 'i';
            break;
        case 0xfb04: // ﬄ	[LATIN SMALL LIGATURE FFL]
            outString += 'f';
            outString += 'f';
            outString += 'l';
            break;
        case 0xfb01: // ﬁ	[LATIN SMALL LIGATURE FI]
            outString += 'f';
            outString += 'i';
            break;
        case 0xfb02: // ﬂ	[LATIN SMALL LIGATURE FL]
            outString += 'f';
            outString += 'l';
            break;
        case 0x11c: // Ĝ	[LATIN CAPITAL LETTER G WITH CIRCUMFLEX]
        case 0x11e: // Ğ	[LATIN CAPITAL LETTER G WITH BREVE]
        case 0x120: // Ġ	[LATIN CAPITAL LETTER G WITH DOT ABOVE]
        case 0x122: // Ģ	[LATIN CAPITAL LETTER G WITH CEDILLA]
        case 0x193: // Ɠ	[LATIN CAPITAL LETTER G WITH HOOK]
        case 0x1e4: // Ǥ	[LATIN CAPITAL LETTER G WITH STROKE]
        case 0x1e5: // ǥ	[LATIN SMALL LETTER G WITH STROKE]
        case 0x1e6: // Ǧ	[LATIN CAPITAL LETTER G WITH CARON]
        case 0x1e7: // ǧ	[LATIN SMALL LETTER G WITH CARON]
        case 0x1f4: // Ǵ	[LATIN CAPITAL LETTER G WITH ACUTE]
        case 0x262: // ɢ	[LATIN LETTER SMALL CAPITAL G]
        case 0x29b: // ʛ	[LATIN LETTER SMALL CAPITAL G WITH HOOK]
        case 0x1e20: // Ḡ	[LATIN CAPITAL LETTER G WITH MACRON]
        case 0x24bc: // Ⓖ	[CIRCLED LATIN CAPITAL LETTER G]
        case 0xa77d: // Ᵹ	[LATIN CAPITAL LETTER INSULAR G]
        case 0xa77e: // Ꝿ	[LATIN CAPITAL LETTER TURNED INSULAR G]
        case 0xff27: // Ｇ	[FULLWIDTH LATIN CAPITAL LETTER G]
            outString += 'G';
            break;
        case 0x11d: // ĝ	[LATIN SMALL LETTER G WITH CIRCUMFLEX]
        case 0x11f: // ğ	[LATIN SMALL LETTER G WITH BREVE]
        case 0x121: // ġ	[LATIN SMALL LETTER G WITH DOT ABOVE]
        case 0x123: // ģ	[LATIN SMALL LETTER G WITH CEDILLA]
        case 0x1f5: // ǵ	[LATIN SMALL LETTER G WITH ACUTE]
        case 0x260: // ɠ	[LATIN SMALL LETTER G WITH HOOK]
        case 0x261: // ɡ	[LATIN SMALL LETTER SCRIPT G]
        case 0x1d77: // ᵷ	[LATIN SMALL LETTER TURNED G]
        case 0x1d79: // ᵹ	[LATIN SMALL LETTER INSULAR G]
        case 0x1d83: // ᶃ	[LATIN SMALL LETTER G WITH PALATAL HOOK]
        case 0x1e21: // ḡ	[LATIN SMALL LETTER G WITH MACRON]
        case 0x24d6: // ⓖ	[CIRCLED LATIN SMALL LETTER G]
        case 0xa77f: // ꝿ	[LATIN SMALL LETTER TURNED INSULAR G]
        case 0xff47: // ｇ	[FULLWIDTH LATIN SMALL LETTER G]
            outString += 'g';
            break;
        case 0x24a2: // ⒢	[PARENTHESIZED LATIN SMALL LETTER G]
            outString += '(';
            outString += 'g';
            outString += ')';
            break;
        case 0x124: // Ĥ	[LATIN CAPITAL LETTER H WITH CIRCUMFLEX]
        case 0x126: // Ħ	[LATIN CAPITAL LETTER H WITH STROKE]
        case 0x21e: // Ȟ	[LATIN CAPITAL LETTER H WITH CARON]
        case 0x29c: // ʜ	[LATIN LETTER SMALL CAPITAL H]
        case 0x1e22: // Ḣ	[LATIN CAPITAL LETTER H WITH DOT ABOVE]
        case 0x1e24: // Ḥ	[LATIN CAPITAL LETTER H WITH DOT BELOW]
        case 0x1e26: // Ḧ	[LATIN CAPITAL LETTER H WITH DIAERESIS]
        case 0x1e28: // Ḩ	[LATIN CAPITAL LETTER H WITH CEDILLA]
        case 0x1e2a: // Ḫ	[LATIN CAPITAL LETTER H WITH BREVE BELOW]
        case 0x24bd: // Ⓗ	[CIRCLED LATIN CAPITAL LETTER H]
        case 0x2c67: // Ⱨ	[LATIN CAPITAL LETTER H WITH DESCENDER]
        case 0x2c75: // Ⱶ	[LATIN CAPITAL LETTER HALF H]
        case 0xff28: // Ｈ	[FULLWIDTH LATIN CAPITAL LETTER H]
            outString += 'H';
            break;
        case 0x125: // ĥ	[LATIN SMALL LETTER H WITH CIRCUMFLEX]
        case 0x127: // ħ	[LATIN SMALL LETTER H WITH STROKE]
        case 0x21f: // ȟ	[LATIN SMALL LETTER H WITH CARON]
        case 0x265: // ɥ	[LATIN SMALL LETTER TURNED H]
        case 0x266: // ɦ	[LATIN SMALL LETTER H WITH HOOK]
        case 0x2ae: // ʮ	[LATIN SMALL LETTER TURNED H WITH FISHHOOK]
        case 0x2af: // ʯ	[LATIN SMALL LETTER TURNED H WITH FISHHOOK AND TAIL]
        case 0x1e23: // ḣ	[LATIN SMALL LETTER H WITH DOT ABOVE]
        case 0x1e25: // ḥ	[LATIN SMALL LETTER H WITH DOT BELOW]
        case 0x1e27: // ḧ	[LATIN SMALL LETTER H WITH DIAERESIS]
        case 0x1e29: // ḩ	[LATIN SMALL LETTER H WITH CEDILLA]
        case 0x1e2b: // ḫ	[LATIN SMALL LETTER H WITH BREVE BELOW]
        case 0x1e96: // ẖ	[LATIN SMALL LETTER H WITH LINE BELOW]
        case 0x24d7: // ⓗ	[CIRCLED LATIN SMALL LETTER H]
        case 0x2c68: // ⱨ	[LATIN SMALL LETTER H WITH DESCENDER]
        case 0x2c76: // ⱶ	[LATIN SMALL LETTER HALF H]
        case 0xff48: // ｈ	[FULLWIDTH LATIN SMALL LETTER H]
            outString += 'h';
            break;
        case 0x1f6: // Ƕ	http;//en.wikipedia.org/wiki/Hwair	[LATIN CAPITAL LETTER HWAIR]
            outString += 'H';
            outString += 'V';
            break;
        case 0x24a3: // ⒣	[PARENTHESIZED LATIN SMALL LETTER H]
            outString += '(';
            outString += 'h';
            outString += ')';
            break;
        case 0x195: // ƕ	[LATIN SMALL LETTER HV]
            outString += 'h';
            outString += 'v';
            break;
        case 0xcc: // Ì	[LATIN CAPITAL LETTER I WITH GRAVE]
        case 0xcd: // Í	[LATIN CAPITAL LETTER I WITH ACUTE]
        case 0xce: // Î	[LATIN CAPITAL LETTER I WITH CIRCUMFLEX]
        case 0xcf: // Ï	[LATIN CAPITAL LETTER I WITH DIAERESIS]
        case 0x128: // Ĩ	[LATIN CAPITAL LETTER I WITH TILDE]
        case 0x12a: // Ī	[LATIN CAPITAL LETTER I WITH MACRON]
        case 0x12c: // Ĭ	[LATIN CAPITAL LETTER I WITH BREVE]
        case 0x12e: // Į	[LATIN CAPITAL LETTER I WITH OGONEK]
        case 0x130: // İ	[LATIN CAPITAL LETTER I WITH DOT ABOVE]
        case 0x196: // Ɩ	[LATIN CAPITAL LETTER IOTA]
        case 0x197: // Ɨ	[LATIN CAPITAL LETTER I WITH STROKE]
        case 0x1cf: // Ǐ	[LATIN CAPITAL LETTER I WITH CARON]
        case 0x208: // Ȉ	[LATIN CAPITAL LETTER I WITH DOUBLE GRAVE]
        case 0x20a: // Ȋ	[LATIN CAPITAL LETTER I WITH INVERTED BREVE]
        case 0x26a: // ɪ	[LATIN LETTER SMALL CAPITAL I]
        case 0x1d7b: // ᵻ	[LATIN SMALL CAPITAL LETTER I WITH STROKE]
        case 0x1e2c: // Ḭ	[LATIN CAPITAL LETTER I WITH TILDE BELOW]
        case 0x1e2e: // Ḯ	[LATIN CAPITAL LETTER I WITH DIAERESIS AND ACUTE]
        case 0x1ec8: // Ỉ	[LATIN CAPITAL LETTER I WITH HOOK ABOVE]
        case 0x1eca: // Ị	[LATIN CAPITAL LETTER I WITH DOT BELOW]
        case 0x24be: // Ⓘ	[CIRCLED LATIN CAPITAL LETTER I]
        case 0xa7fe: // ꟾ	[LATIN EPIGRAPHIC LETTER I LONGA]
        case 0xff29: // Ｉ	[FULLWIDTH LATIN CAPITAL LETTER I]
            outString += 'I';
            break;
        case 0xec: // ì	[LATIN SMALL LETTER I WITH GRAVE]
        case 0xed: // í	[LATIN SMALL LETTER I WITH ACUTE]
        case 0xee: // î	[LATIN SMALL LETTER I WITH CIRCUMFLEX]
        case 0xef: // ï	[LATIN SMALL LETTER I WITH DIAERESIS]
        case 0x129: // ĩ	[LATIN SMALL LETTER I WITH TILDE]
        case 0x12b: // ī	[LATIN SMALL LETTER I WITH MACRON]
        case 0x12d: // ĭ	[LATIN SMALL LETTER I WITH BREVE]
        case 0x12f: // į	[LATIN SMALL LETTER I WITH OGONEK]
        case 0x131: // ı	[LATIN SMALL LETTER DOTLESS I]
        case 0x1d0: // ǐ	[LATIN SMALL LETTER I WITH CARON]
        case 0x209: // ȉ	[LATIN SMALL LETTER I WITH DOUBLE GRAVE]
        case 0x20b: // ȋ	[LATIN SMALL LETTER I WITH INVERTED BREVE]
        case 0x268: // ɨ	[LATIN SMALL LETTER I WITH STROKE]
        case 0x1d09: // ᴉ	[LATIN SMALL LETTER TURNED I]
        case 0x1d62: // ᵢ	[LATIN SUBSCRIPT SMALL LETTER I]
        case 0x1d7c: // ᵼ	[LATIN SMALL LETTER IOTA WITH STROKE]
        case 0x1d96: // ᶖ	[LATIN SMALL LETTER I WITH RETROFLEX HOOK]
        case 0x1e2d: // ḭ	[LATIN SMALL LETTER I WITH TILDE BELOW]
        case 0x1e2f: // ḯ	[LATIN SMALL LETTER I WITH DIAERESIS AND ACUTE]
        case 0x1ec9: // ỉ	[LATIN SMALL LETTER I WITH HOOK ABOVE]
        case 0x1ecb: // ị	[LATIN SMALL LETTER I WITH DOT BELOW]
        case 0x2071: // ⁱ	[SUPERSCRIPT LATIN SMALL LETTER I]
        case 0x24d8: // ⓘ	[CIRCLED LATIN SMALL LETTER I]
        case 0xff49: // ｉ	[FULLWIDTH LATIN SMALL LETTER I]
            outString += 'i';
            break;
        case 0x132: // Ĳ	[LATIN CAPITAL LIGATURE IJ]
            outString += 'I';
            outString += 'J';
            break;
        case 0x24a4: // ⒤	[PARENTHESIZED LATIN SMALL LETTER I]
            outString += '(';
            outString += 'i';
            outString += ')';
            break;
        case 0x133: // ĳ	[LATIN SMALL LIGATURE IJ]
            outString += 'i';
            outString += 'j';
            break;
        case 0x134: // Ĵ	[LATIN CAPITAL LETTER J WITH CIRCUMFLEX]
        case 0x248: // Ɉ	[LATIN CAPITAL LETTER J WITH STROKE]
        case 0x1d0a: // ᴊ	[LATIN LETTER SMALL CAPITAL J]
        case 0x24bf: // Ⓙ	[CIRCLED LATIN CAPITAL LETTER J]
        case 0xff2a: // Ｊ	[FULLWIDTH LATIN CAPITAL LETTER J]
            outString += 'J';
            break;
        case 0x135: // ĵ	[LATIN SMALL LETTER J WITH CIRCUMFLEX]
        case 0x1f0: // ǰ	[LATIN SMALL LETTER J WITH CARON]
        case 0x237: // ȷ	[LATIN SMALL LETTER DOTLESS J]
        case 0x249: // ɉ	[LATIN SMALL LETTER J WITH STROKE]
        case 0x25f: // ɟ	[LATIN SMALL LETTER DOTLESS J WITH STROKE]
        case 0x284: // ʄ	[LATIN SMALL LETTER DOTLESS J WITH STROKE AND HOOK]
        case 0x29d: // ʝ	[LATIN SMALL LETTER J WITH CROSSED-TAIL]
        case 0x24d9: // ⓙ	[CIRCLED LATIN SMALL LETTER J]
        case 0x2c7c: // ⱼ	[LATIN SUBSCRIPT SMALL LETTER J]
        case 0xff4a: // ｊ	[FULLWIDTH LATIN SMALL LETTER J]
            outString += 'j';
            break;
        case 0x24a5: // ⒥	[PARENTHESIZED LATIN SMALL LETTER J]
            outString += '(';
            outString += 'j';
            outString += ')';
            break;
        case 0x136: // Ķ	[LATIN CAPITAL LETTER K WITH CEDILLA]
        case 0x198: // Ƙ	[LATIN CAPITAL LETTER K WITH HOOK]
        case 0x1e8: // Ǩ	[LATIN CAPITAL LETTER K WITH CARON]
        case 0x1d0b: // ᴋ	[LATIN LETTER SMALL CAPITAL K]
        case 0x1e30: // Ḱ	[LATIN CAPITAL LETTER K WITH ACUTE]
        case 0x1e32: // Ḳ	[LATIN CAPITAL LETTER K WITH DOT BELOW]
        case 0x1e34: // Ḵ	[LATIN CAPITAL LETTER K WITH LINE BELOW]
        case 0x24c0: // Ⓚ	[CIRCLED LATIN CAPITAL LETTER K]
        case 0x2c69: // Ⱪ	[LATIN CAPITAL LETTER K WITH DESCENDER]
        case 0xa740: // Ꝁ	[LATIN CAPITAL LETTER K WITH STROKE]
        case 0xa742: // Ꝃ	[LATIN CAPITAL LETTER K WITH DIAGONAL STROKE]
        case 0xa744: // Ꝅ	[LATIN CAPITAL LETTER K WITH STROKE AND DIAGONAL STROKE]
        case 0xff2b: // Ｋ	[FULLWIDTH LATIN CAPITAL LETTER K]
            outString += 'K';
            break;
        case 0x137: // ķ	[LATIN SMALL LETTER K WITH CEDILLA]
        case 0x199: // ƙ	[LATIN SMALL LETTER K WITH HOOK]
        case 0x1e9: // ǩ	[LATIN SMALL LETTER K WITH CARON]
        case 0x29e: // ʞ	[LATIN SMALL LETTER TURNED K]
        case 0x1d84: // ᶄ	[LATIN SMALL LETTER K WITH PALATAL HOOK]
        case 0x1e31: // ḱ	[LATIN SMALL LETTER K WITH ACUTE]
        case 0x1e33: // ḳ	[LATIN SMALL LETTER K WITH DOT BELOW]
        case 0x1e35: // ḵ	[LATIN SMALL LETTER K WITH LINE BELOW]
        case 0x24da: // ⓚ	[CIRCLED LATIN SMALL LETTER K]
        case 0x2c6a: // ⱪ	[LATIN SMALL LETTER K WITH DESCENDER]
        case 0xa741: // ꝁ	[LATIN SMALL LETTER K WITH STROKE]
        case 0xa743: // ꝃ	[LATIN SMALL LETTER K WITH DIAGONAL STROKE]
        case 0xa745: // ꝅ	[LATIN SMALL LETTER K WITH STROKE AND DIAGONAL STROKE]
        case 0xff4b: // ｋ	[FULLWIDTH LATIN SMALL LETTER K]
            outString += 'k';
            break;
        case 0x24a6: // ⒦	[PARENTHESIZED LATIN SMALL LETTER K]
            outString += '(';
            outString += 'k';
            outString += ')';
            break;
        case 0x139: // Ĺ	[LATIN CAPITAL LETTER L WITH ACUTE]
        case 0x13b: // Ļ	[LATIN CAPITAL LETTER L WITH CEDILLA]
        case 0x13d: // Ľ	[LATIN CAPITAL LETTER L WITH CARON]
        case 0x13f: // Ŀ	[LATIN CAPITAL LETTER L WITH MIDDLE DOT]
        case 0x141: // Ł	[LATIN CAPITAL LETTER L WITH STROKE]
        case 0x23d: // Ƚ	[LATIN CAPITAL LETTER L WITH BAR]
        case 0x29f: // ʟ	[LATIN LETTER SMALL CAPITAL L]
        case 0x1d0c: // ᴌ	[LATIN LETTER SMALL CAPITAL L WITH STROKE]
        case 0x1e36: // Ḷ	[LATIN CAPITAL LETTER L WITH DOT BELOW]
        case 0x1e38: // Ḹ	[LATIN CAPITAL LETTER L WITH DOT BELOW AND MACRON]
        case 0x1e3a: // Ḻ	[LATIN CAPITAL LETTER L WITH LINE BELOW]
        case 0x1e3c: // Ḽ	[LATIN CAPITAL LETTER L WITH CIRCUMFLEX BELOW]
        case 0x24c1: // Ⓛ	[CIRCLED LATIN CAPITAL LETTER L]
        case 0x2c60: // Ⱡ	[LATIN CAPITAL LETTER L WITH DOUBLE BAR]
        case 0x2c62: // Ɫ	[LATIN CAPITAL LETTER L WITH MIDDLE TILDE]
        case 0xa746: // Ꝇ	[LATIN CAPITAL LETTER BROKEN L]
        case 0xa748: // Ꝉ	[LATIN CAPITAL LETTER L WITH HIGH STROKE]
        case 0xa780: // Ꞁ	[LATIN CAPITAL LETTER TURNED L]
        case 0xff2c: // Ｌ	[FULLWIDTH LATIN CAPITAL LETTER L]
            outString += 'L';
            break;
        case 0x13a: // ĺ	[LATIN SMALL LETTER L WITH ACUTE]
        case 0x13c: // ļ	[LATIN SMALL LETTER L WITH CEDILLA]
        case 0x13e: // ľ	[LATIN SMALL LETTER L WITH CARON]
        case 0x140: // ŀ	[LATIN SMALL LETTER L WITH MIDDLE DOT]
        case 0x142: // ł	[LATIN SMALL LETTER L WITH STROKE]
        case 0x19a: // ƚ	[LATIN SMALL LETTER L WITH BAR]
        case 0x234: // ȴ	[LATIN SMALL LETTER L WITH CURL]
        case 0x26b: // ɫ	[LATIN SMALL LETTER L WITH MIDDLE TILDE]
        case 0x26c: // ɬ	[LATIN SMALL LETTER L WITH BELT]
        case 0x26d: // ɭ	[LATIN SMALL LETTER L WITH RETROFLEX HOOK]
        case 0x1d85: // ᶅ	[LATIN SMALL LETTER L WITH PALATAL HOOK]
        case 0x1e37: // ḷ	[LATIN SMALL LETTER L WITH DOT BELOW]
        case 0x1e39: // ḹ	[LATIN SMALL LETTER L WITH DOT BELOW AND MACRON]
        case 0x1e3b: // ḻ	[LATIN SMALL LETTER L WITH LINE BELOW]
        case 0x1e3d: // ḽ	[LATIN SMALL LETTER L WITH CIRCUMFLEX BELOW]
        case 0x24db: // ⓛ	[CIRCLED LATIN SMALL LETTER L]
        case 0x2c61: // ⱡ	[LATIN SMALL LETTER L WITH DOUBLE BAR]
        case 0xa747: // ꝇ	[LATIN SMALL LETTER BROKEN L]
        case 0xa749: // ꝉ	[LATIN SMALL LETTER L WITH HIGH STROKE]
        case 0xa781: // ꞁ	[LATIN SMALL LETTER TURNED L]
        case 0xff4c: // ｌ	[FULLWIDTH LATIN SMALL LETTER L]
            outString += 'l';
            break;
        case 0x1c7: // Ǉ	[LATIN CAPITAL LETTER LJ]
            outString += 'L';
            outString += 'J';
            break;
        case 0x1efa: // Ỻ	[LATIN CAPITAL LETTER MIDDLE-WELSH LL]
            outString += 'L';
            outString += 'L';
            break;
        case 0x1c8: // ǈ	[LATIN CAPITAL LETTER L WITH SMALL LETTER J]
            outString += 'L';
            outString += 'j';
            break;
        case 0x24a7: // ⒧	[PARENTHESIZED LATIN SMALL LETTER L]
            outString += '(';
            outString += 'l';
            outString += ')';
            break;
        case 0x1c9: // ǉ	[LATIN SMALL LETTER LJ]
            outString += 'l';
            outString += 'j';
            break;
        case 0x1efb: // ỻ	[LATIN SMALL LETTER MIDDLE-WELSH LL]
            outString += 'l';
            outString += 'l';
            break;
        case 0x2aa: // ʪ	[LATIN SMALL LETTER LS DIGRAPH]
            outString += 'l';
            outString += 's';
            break;
        case 0x2ab: // ʫ	[LATIN SMALL LETTER LZ DIGRAPH]
            outString += 'l';
            outString += 'z';
            break;
        case 0x19c: // Ɯ	[LATIN CAPITAL LETTER TURNED M]
        case 0x1d0d: // ᴍ	[LATIN LETTER SMALL CAPITAL M]
        case 0x1e3e: // Ḿ	[LATIN CAPITAL LETTER M WITH ACUTE]
        case 0x1e40: // Ṁ	[LATIN CAPITAL LETTER M WITH DOT ABOVE]
        case 0x1e42: // Ṃ	[LATIN CAPITAL LETTER M WITH DOT BELOW]
        case 0x24c2: // Ⓜ	[CIRCLED LATIN CAPITAL LETTER M]
        case 0x2c6e: // Ɱ	[LATIN CAPITAL LETTER M WITH HOOK]
        case 0xa7fd: // ꟽ	[LATIN EPIGRAPHIC LETTER INVERTED M]
        case 0xa7ff: // ꟿ	[LATIN EPIGRAPHIC LETTER ARCHAIC M]
        case 0xff2d: // Ｍ	[FULLWIDTH LATIN CAPITAL LETTER M]
            outString += 'M';
            break;
        case 0x26f: // ɯ	[LATIN SMALL LETTER TURNED M]
        case 0x270: // ɰ	[LATIN SMALL LETTER TURNED M WITH LONG LEG]
        case 0x271: // ɱ	[LATIN SMALL LETTER M WITH HOOK]
        case 0x1d6f: // ᵯ	[LATIN SMALL LETTER M WITH MIDDLE TILDE]
        case 0x1d86: // ᶆ	[LATIN SMALL LETTER M WITH PALATAL HOOK]
        case 0x1e3f: // ḿ	[LATIN SMALL LETTER M WITH ACUTE]
        case 0x1e41: // ṁ	[LATIN SMALL LETTER M WITH DOT ABOVE]
        case 0x1e43: // ṃ	[LATIN SMALL LETTER M WITH DOT BELOW]
        case 0x24dc: // ⓜ	[CIRCLED LATIN SMALL LETTER M]
        case 0xff4d: // ｍ	[FULLWIDTH LATIN SMALL LETTER M]
            outString += 'm';
            break;
        case 0x24a8: // ⒨	[PARENTHESIZED LATIN SMALL LETTER M]
            outString += '(';
            outString += 'm';
            outString += ')';
            break;
        case 0xd1: // Ñ	[LATIN CAPITAL LETTER N WITH TILDE]
        case 0x143: // Ń	[LATIN CAPITAL LETTER N WITH ACUTE]
        case 0x145: // Ņ	[LATIN CAPITAL LETTER N WITH CEDILLA]
        case 0x147: // Ň	[LATIN CAPITAL LETTER N WITH CARON]
        case 0x14a: // Ŋ	http;//en.wikipedia.org/wiki/Eng_(letter)	[LATIN CAPITAL LETTER ENG]
        case 0x19d: // Ɲ	[LATIN CAPITAL LETTER N WITH LEFT HOOK]
        case 0x1f8: // Ǹ	[LATIN CAPITAL LETTER N WITH GRAVE]
        case 0x220: // Ƞ	[LATIN CAPITAL LETTER N WITH LONG RIGHT LEG]
        case 0x274: // ɴ	[LATIN LETTER SMALL CAPITAL N]
        case 0x1d0e: // ᴎ	[LATIN LETTER SMALL CAPITAL REVERSED N]
        case 0x1e44: // Ṅ	[LATIN CAPITAL LETTER N WITH DOT ABOVE]
        case 0x1e46: // Ṇ	[LATIN CAPITAL LETTER N WITH DOT BELOW]
        case 0x1e48: // Ṉ	[LATIN CAPITAL LETTER N WITH LINE BELOW]
        case 0x1e4a: // Ṋ	[LATIN CAPITAL LETTER N WITH CIRCUMFLEX BELOW]
        case 0x24c3: // Ⓝ	[CIRCLED LATIN CAPITAL LETTER N]
        case 0xff2e: // Ｎ	[FULLWIDTH LATIN CAPITAL LETTER N]
            outString += 'N';
            break;
        case 0xf1: // ñ	[LATIN SMALL LETTER N WITH TILDE]
        case 0x144: // ń	[LATIN SMALL LETTER N WITH ACUTE]
        case 0x146: // ņ	[LATIN SMALL LETTER N WITH CEDILLA]
        case 0x148: // ň	[LATIN SMALL LETTER N WITH CARON]
        case 0x149: // ŉ	[LATIN SMALL LETTER N PRECEDED BY APOSTROPHE]
        case 0x14b: // ŋ	http;//en.wikipedia.org/wiki/Eng_(letter)	[LATIN SMALL LETTER ENG]
        case 0x19e: // ƞ	[LATIN SMALL LETTER N WITH LONG RIGHT LEG]
        case 0x1f9: // ǹ	[LATIN SMALL LETTER N WITH GRAVE]
        case 0x235: // ȵ	[LATIN SMALL LETTER N WITH CURL]
        case 0x272: // ɲ	[LATIN SMALL LETTER N WITH LEFT HOOK]
        case 0x273: // ɳ	[LATIN SMALL LETTER N WITH RETROFLEX HOOK]
        case 0x1d70: // ᵰ	[LATIN SMALL LETTER N WITH MIDDLE TILDE]
        case 0x1d87: // ᶇ	[LATIN SMALL LETTER N WITH PALATAL HOOK]
        case 0x1e45: // ṅ	[LATIN SMALL LETTER N WITH DOT ABOVE]
        case 0x1e47: // ṇ	[LATIN SMALL LETTER N WITH DOT BELOW]
        case 0x1e49: // ṉ	[LATIN SMALL LETTER N WITH LINE BELOW]
        case 0x1e4b: // ṋ	[LATIN SMALL LETTER N WITH CIRCUMFLEX BELOW]
        case 0x207f: // ⁿ	[SUPERSCRIPT LATIN SMALL LETTER N]
        case 0x24dd: // ⓝ	[CIRCLED LATIN SMALL LETTER N]
        case 0xff4e: // ｎ	[FULLWIDTH LATIN SMALL LETTER N]
            outString += 'n';
            break;
        case 0x1ca: // Ǌ	[LATIN CAPITAL LETTER NJ]
            outString += 'N';
            outString += 'J';
            break;
        case 0x1cb: // ǋ	[LATIN CAPITAL LETTER N WITH SMALL LETTER J]
            outString += 'N';
            outString += 'j';
            break;
        case 0x24a9: // ⒩	[PARENTHESIZED LATIN SMALL LETTER N]
            outString += '(';
            outString += 'n';
            outString += ')';
            break;
        case 0x1cc: // ǌ	[LATIN SMALL LETTER NJ]
            outString += 'n';
            outString += 'j';
            break;
        case 0xd2: // Ò	[LATIN CAPITAL LETTER O WITH GRAVE]
        case 0xd3: // Ó	[LATIN CAPITAL LETTER O WITH ACUTE]
        case 0xd4: // Ô	[LATIN CAPITAL LETTER O WITH CIRCUMFLEX]
        case 0xd5: // Õ	[LATIN CAPITAL LETTER O WITH TILDE]
        case 0xd6: // Ö	[LATIN CAPITAL LETTER O WITH DIAERESIS]
        case 0xd8: // Ø	[LATIN CAPITAL LETTER O WITH STROKE]
        case 0x14c: // Ō	[LATIN CAPITAL LETTER O WITH MACRON]
        case 0x14e: // Ŏ	[LATIN CAPITAL LETTER O WITH BREVE]
        case 0x150: // Ő	[LATIN CAPITAL LETTER O WITH DOUBLE ACUTE]
        case 0x186: // Ɔ	[LATIN CAPITAL LETTER OPEN O]
        case 0x19f: // Ɵ	[LATIN CAPITAL LETTER O WITH MIDDLE TILDE]
        case 0x1a0: // Ơ	[LATIN CAPITAL LETTER O WITH HORN]
        case 0x1d1: // Ǒ	[LATIN CAPITAL LETTER O WITH CARON]
        case 0x1ea: // Ǫ	[LATIN CAPITAL LETTER O WITH OGONEK]
        case 0x1ec: // Ǭ	[LATIN CAPITAL LETTER O WITH OGONEK AND MACRON]
        case 0x1fe: // Ǿ	[LATIN CAPITAL LETTER O WITH STROKE AND ACUTE]
        case 0x20c: // Ȍ	[LATIN CAPITAL LETTER O WITH DOUBLE GRAVE]
        case 0x20e: // Ȏ	[LATIN CAPITAL LETTER O WITH INVERTED BREVE]
        case 0x22a: // Ȫ	[LATIN CAPITAL LETTER O WITH DIAERESIS AND MACRON]
        case 0x22c: // Ȭ	[LATIN CAPITAL LETTER O WITH TILDE AND MACRON]
        case 0x22e: // Ȯ	[LATIN CAPITAL LETTER O WITH DOT ABOVE]
        case 0x230: // Ȱ	[LATIN CAPITAL LETTER O WITH DOT ABOVE AND MACRON]
        case 0x1d0f: // ᴏ	[LATIN LETTER SMALL CAPITAL O]
        case 0x1d10: // ᴐ	[LATIN LETTER SMALL CAPITAL OPEN O]
        case 0x1e4c: // Ṍ	[LATIN CAPITAL LETTER O WITH TILDE AND ACUTE]
        case 0x1e4e: // Ṏ	[LATIN CAPITAL LETTER O WITH TILDE AND DIAERESIS]
        case 0x1e50: // Ṑ	[LATIN CAPITAL LETTER O WITH MACRON AND GRAVE]
        case 0x1e52: // Ṓ	[LATIN CAPITAL LETTER O WITH MACRON AND ACUTE]
        case 0x1ecc: // Ọ	[LATIN CAPITAL LETTER O WITH DOT BELOW]
        case 0x1ece: // Ỏ	[LATIN CAPITAL LETTER O WITH HOOK ABOVE]
        case 0x1ed0: // Ố	[LATIN CAPITAL LETTER O WITH CIRCUMFLEX AND ACUTE]
        case 0x1ed2: // Ồ	[LATIN CAPITAL LETTER O WITH CIRCUMFLEX AND GRAVE]
        case 0x1ed4: // Ổ	[LATIN CAPITAL LETTER O WITH CIRCUMFLEX AND HOOK ABOVE]
        case 0x1ed6: // Ỗ	[LATIN CAPITAL LETTER O WITH CIRCUMFLEX AND TILDE]
        case 0x1ed8: // Ộ	[LATIN CAPITAL LETTER O WITH CIRCUMFLEX AND DOT BELOW]
        case 0x1eda: // Ớ	[LATIN CAPITAL LETTER O WITH HORN AND ACUTE]
        case 0x1edc: // Ờ	[LATIN CAPITAL LETTER O WITH HORN AND GRAVE]
        case 0x1ede: // Ở	[LATIN CAPITAL LETTER O WITH HORN AND HOOK ABOVE]
        case 0x1ee0: // Ỡ	[LATIN CAPITAL LETTER O WITH HORN AND TILDE]
        case 0x1ee2: // Ợ	[LATIN CAPITAL LETTER O WITH HORN AND DOT BELOW]
        case 0x24c4: // Ⓞ	[CIRCLED LATIN CAPITAL LETTER O]
        case 0xa74a: // Ꝋ	[LATIN CAPITAL LETTER O WITH LONG STROKE OVERLAY]
        case 0xa74c: // Ꝍ	[LATIN CAPITAL LETTER O WITH LOOP]
        case 0xff2f: // Ｏ	[FULLWIDTH LATIN CAPITAL LETTER O]
            outString += 'O';
            break;
        case 0xf2: // ò	[LATIN SMALL LETTER O WITH GRAVE]
        case 0xf3: // ó	[LATIN SMALL LETTER O WITH ACUTE]
        case 0xf4: // ô	[LATIN SMALL LETTER O WITH CIRCUMFLEX]
        case 0xf5: // õ	[LATIN SMALL LETTER O WITH TILDE]
        case 0xf6: // ö	[LATIN SMALL LETTER O WITH DIAERESIS]
        case 0xf8: // ø	[LATIN SMALL LETTER O WITH STROKE]
        case 0x14d: // ō	[LATIN SMALL LETTER O WITH MACRON]
        case 0x14f: // ŏ	[LATIN SMALL LETTER O WITH BREVE]
        case 0x151: // ő	[LATIN SMALL LETTER O WITH DOUBLE ACUTE]
        case 0x1a1: // ơ	[LATIN SMALL LETTER O WITH HORN]
        case 0x1d2: // ǒ	[LATIN SMALL LETTER O WITH CARON]
        case 0x1eb: // ǫ	[LATIN SMALL LETTER O WITH OGONEK]
        case 0x1ed: // ǭ	[LATIN SMALL LETTER O WITH OGONEK AND MACRON]
        case 0x1ff: // ǿ	[LATIN SMALL LETTER O WITH STROKE AND ACUTE]
        case 0x20d: // ȍ	[LATIN SMALL LETTER O WITH DOUBLE GRAVE]
        case 0x20f: // ȏ	[LATIN SMALL LETTER O WITH INVERTED BREVE]
        case 0x22b: // ȫ	[LATIN SMALL LETTER O WITH DIAERESIS AND MACRON]
        case 0x22d: // ȭ	[LATIN SMALL LETTER O WITH TILDE AND MACRON]
        case 0x22f: // ȯ	[LATIN SMALL LETTER O WITH DOT ABOVE]
        case 0x231: // ȱ	[LATIN SMALL LETTER O WITH DOT ABOVE AND MACRON]
        case 0x254: // ɔ	[LATIN SMALL LETTER OPEN O]
        case 0x275: // ɵ	[LATIN SMALL LETTER BARRED O]
        case 0x1d16: // ᴖ	[LATIN SMALL LETTER TOP HALF O]
        case 0x1d17: // ᴗ	[LATIN SMALL LETTER BOTTOM HALF O]
        case 0x1d97: // ᶗ	[LATIN SMALL LETTER OPEN O WITH RETROFLEX HOOK]
        case 0x1e4d: // ṍ	[LATIN SMALL LETTER O WITH TILDE AND ACUTE]
        case 0x1e4f: // ṏ	[LATIN SMALL LETTER O WITH TILDE AND DIAERESIS]
        case 0x1e51: // ṑ	[LATIN SMALL LETTER O WITH MACRON AND GRAVE]
        case 0x1e53: // ṓ	[LATIN SMALL LETTER O WITH MACRON AND ACUTE]
        case 0x1ecd: // ọ	[LATIN SMALL LETTER O WITH DOT BELOW]
        case 0x1ecf: // ỏ	[LATIN SMALL LETTER O WITH HOOK ABOVE]
        case 0x1ed1: // ố	[LATIN SMALL LETTER O WITH CIRCUMFLEX AND ACUTE]
        case 0x1ed3: // ồ	[LATIN SMALL LETTER O WITH CIRCUMFLEX AND GRAVE]
        case 0x1ed5: // ổ	[LATIN SMALL LETTER O WITH CIRCUMFLEX AND HOOK ABOVE]
        case 0x1ed7: // ỗ	[LATIN SMALL LETTER O WITH CIRCUMFLEX AND TILDE]
        case 0x1ed9: // ộ	[LATIN SMALL LETTER O WITH CIRCUMFLEX AND DOT BELOW]
        case 0x1edb: // ớ	[LATIN SMALL LETTER O WITH HORN AND ACUTE]
        case 0x1edd: // ờ	[LATIN SMALL LETTER O WITH HORN AND GRAVE]
        case 0x1edf: // ở	[LATIN SMALL LETTER O WITH HORN AND HOOK ABOVE]
        case 0x1ee1: // ỡ	[LATIN SMALL LETTER O WITH HORN AND TILDE]
        case 0x1ee3: // ợ	[LATIN SMALL LETTER O WITH HORN AND DOT BELOW]
        case 0x2092: // ₒ	[LATIN SUBSCRIPT SMALL LETTER O]
        case 0x24de: // ⓞ	[CIRCLED LATIN SMALL LETTER O]
        case 0x2c7a: // ⱺ	[LATIN SMALL LETTER O WITH LOW RING INSIDE]
        case 0xa74b: // ꝋ	[LATIN SMALL LETTER O WITH LONG STROKE OVERLAY]
        case 0xa74d: // ꝍ	[LATIN SMALL LETTER O WITH LOOP]
        case 0xff4f: // ｏ	[FULLWIDTH LATIN SMALL LETTER O]
            outString += 'o';
            break;
        case 0x152: // Œ	[LATIN CAPITAL LIGATURE OE]
        case 0x276: // ɶ	[LATIN LETTER SMALL CAPITAL OE]
            outString += 'O';
            outString += 'E';
            break;
        case 0xa74e: // Ꝏ	[LATIN CAPITAL LETTER OO]
            outString += 'O';
            outString += 'O';
            break;
        case 0x222: // Ȣ	http;//en.wikipedia.org/wiki/OU	[LATIN CAPITAL LETTER OU]
        case 0x1d15: // ᴕ	[LATIN LETTER SMALL CAPITAL OU]
            outString += 'O';
            outString += 'U';
            break;
        case 0x24aa: // ⒪	[PARENTHESIZED LATIN SMALL LETTER O]
            outString += '(';
            outString += 'o';
            outString += ')';
            break;
        case 0x153: // œ	[LATIN SMALL LIGATURE OE]
        case 0x1d14: // ᴔ	[LATIN SMALL LETTER TURNED OE]
            outString += 'o';
            outString += 'e';
            break;
        case 0xa74f: // ꝏ	[LATIN SMALL LETTER OO]
            outString += 'o';
            outString += 'o';
            break;
        case 0x223: // ȣ	http;//en.wikipedia.org/wiki/OU	[LATIN SMALL LETTER OU]
            outString += 'o';
            outString += 'u';
            break;
        case 0x1a4: // Ƥ	[LATIN CAPITAL LETTER P WITH HOOK]
        case 0x1d18: // ᴘ	[LATIN LETTER SMALL CAPITAL P]
        case 0x1e54: // Ṕ	[LATIN CAPITAL LETTER P WITH ACUTE]
        case 0x1e56: // Ṗ	[LATIN CAPITAL LETTER P WITH DOT ABOVE]
        case 0x24c5: // Ⓟ	[CIRCLED LATIN CAPITAL LETTER P]
        case 0x2c63: // Ᵽ	[LATIN CAPITAL LETTER P WITH STROKE]
        case 0xa750: // Ꝑ	[LATIN CAPITAL LETTER P WITH STROKE THROUGH DESCENDER]
        case 0xa752: // Ꝓ	[LATIN CAPITAL LETTER P WITH FLOURISH]
        case 0xa754: // Ꝕ	[LATIN CAPITAL LETTER P WITH SQUIRREL TAIL]
        case 0xff30: // Ｐ	[FULLWIDTH LATIN CAPITAL LETTER P]
            outString += 'P';
            break;
        case 0x1a5: // ƥ	[LATIN SMALL LETTER P WITH HOOK]
        case 0x1d71: // ᵱ	[LATIN SMALL LETTER P WITH MIDDLE TILDE]
        case 0x1d7d: // ᵽ	[LATIN SMALL LETTER P WITH STROKE]
        case 0x1d88: // ᶈ	[LATIN SMALL LETTER P WITH PALATAL HOOK]
        case 0x1e55: // ṕ	[LATIN SMALL LETTER P WITH ACUTE]
        case 0x1e57: // ṗ	[LATIN SMALL LETTER P WITH DOT ABOVE]
        case 0x24df: // ⓟ	[CIRCLED LATIN SMALL LETTER P]
        case 0xa751: // ꝑ	[LATIN SMALL LETTER P WITH STROKE THROUGH DESCENDER]
        case 0xa753: // ꝓ	[LATIN SMALL LETTER P WITH FLOURISH]
        case 0xa755: // ꝕ	[LATIN SMALL LETTER P WITH SQUIRREL TAIL]
        case 0xa7fc: // ꟼ	[LATIN EPIGRAPHIC LETTER REVERSED P]
        case 0xff50: // ｐ	[FULLWIDTH LATIN SMALL LETTER P]
            outString += 'p';
            break;
        case 0x24ab: // ⒫	[PARENTHESIZED LATIN SMALL LETTER P]
            outString += '(';
            outString += 'p';
            outString += ')';
            break;
        case 0x24a: // Ɋ	[LATIN CAPITAL LETTER SMALL Q WITH HOOK TAIL]
        case 0x24c6: // Ⓠ	[CIRCLED LATIN CAPITAL LETTER Q]
        case 0xa756: // Ꝗ	[LATIN CAPITAL LETTER Q WITH STROKE THROUGH DESCENDER]
        case 0xa758: // Ꝙ	[LATIN CAPITAL LETTER Q WITH DIAGONAL STROKE]
        case 0xff31: // Ｑ	[FULLWIDTH LATIN CAPITAL LETTER Q]
            outString += 'Q';
            break;
        case 0x138: // ĸ	http;//en.wikipedia.org/wiki/Kra_(letter)	[LATIN SMALL LETTER KRA]
        case 0x24b: // ɋ	[LATIN SMALL LETTER Q WITH HOOK TAIL]
        case 0x2a0: // ʠ	[LATIN SMALL LETTER Q WITH HOOK]
        case 0x24e0: // ⓠ	[CIRCLED LATIN SMALL LETTER Q]
        case 0xa757: // ꝗ	[LATIN SMALL LETTER Q WITH STROKE THROUGH DESCENDER]
        case 0xa759: // ꝙ	[LATIN SMALL LETTER Q WITH DIAGONAL STROKE]
        case 0xff51: // ｑ	[FULLWIDTH LATIN SMALL LETTER Q]
            outString += 'q';
            break;
        case 0x24ac: // ⒬	[PARENTHESIZED LATIN SMALL LETTER Q]
            outString += '(';
            outString += 'q';
            outString += ')';
            break;
        case 0x239: // ȹ	[LATIN SMALL LETTER QP DIGRAPH]
            outString += 'q';
            outString += 'p';
            break;
        case 0x154: // Ŕ	[LATIN CAPITAL LETTER R WITH ACUTE]
        case 0x156: // Ŗ	[LATIN CAPITAL LETTER R WITH CEDILLA]
        case 0x158: // Ř	[LATIN CAPITAL LETTER R WITH CARON]
        case 0x210: // Ȓ	[LATIN CAPITAL LETTER R WITH DOUBLE GRAVE]
        case 0x212: // Ȓ	[LATIN CAPITAL LETTER R WITH INVERTED BREVE]
        case 0x24c: // Ɍ	[LATIN CAPITAL LETTER R WITH STROKE]
        case 0x280: // ʀ	[LATIN LETTER SMALL CAPITAL R]
        case 0x281: // ʁ	[LATIN LETTER SMALL CAPITAL INVERTED R]
        case 0x1d19: // ᴙ	[LATIN LETTER SMALL CAPITAL REVERSED R]
        case 0x1d1a: // ᴚ	[LATIN LETTER SMALL CAPITAL TURNED R]
        case 0x1e58: // Ṙ	[LATIN CAPITAL LETTER R WITH DOT ABOVE]
        case 0x1e5a: // Ṛ	[LATIN CAPITAL LETTER R WITH DOT BELOW]
        case 0x1e5c: // Ṝ	[LATIN CAPITAL LETTER R WITH DOT BELOW AND MACRON]
        case 0x1e5e: // Ṟ	[LATIN CAPITAL LETTER R WITH LINE BELOW]
        case 0x24c7: // Ⓡ	[CIRCLED LATIN CAPITAL LETTER R]
        case 0x2c64: // Ɽ	[LATIN CAPITAL LETTER R WITH TAIL]
        case 0xa75a: // Ꝛ	[LATIN CAPITAL LETTER R ROTUNDA]
        case 0xa782: // Ꞃ	[LATIN CAPITAL LETTER INSULAR R]
        case 0xff32: // Ｒ	[FULLWIDTH LATIN CAPITAL LETTER R]
            outString += 'R';
            break;
        case 0x155: // ŕ	[LATIN SMALL LETTER R WITH ACUTE]
        case 0x157: // ŗ	[LATIN SMALL LETTER R WITH CEDILLA]
        case 0x159: // ř	[LATIN SMALL LETTER R WITH CARON]
        case 0x211: // ȑ	[LATIN SMALL LETTER R WITH DOUBLE GRAVE]
        case 0x213: // ȓ	[LATIN SMALL LETTER R WITH INVERTED BREVE]
        case 0x24d: // ɍ	[LATIN SMALL LETTER R WITH STROKE]
        case 0x27c: // ɼ	[LATIN SMALL LETTER R WITH LONG LEG]
        case 0x27d: // ɽ	[LATIN SMALL LETTER R WITH TAIL]
        case 0x27e: // ɾ	[LATIN SMALL LETTER R WITH FISHHOOK]
        case 0x27f: // ɿ	[LATIN SMALL LETTER REVERSED R WITH FISHHOOK]
        case 0x1d63: // ᵣ	[LATIN SUBSCRIPT SMALL LETTER R]
        case 0x1d72: // ᵲ	[LATIN SMALL LETTER R WITH MIDDLE TILDE]
        case 0x1d73: // ᵳ	[LATIN SMALL LETTER R WITH FISHHOOK AND MIDDLE TILDE]
        case 0x1d89: // ᶉ	[LATIN SMALL LETTER R WITH PALATAL HOOK]
        case 0x1e59: // ṙ	[LATIN SMALL LETTER R WITH DOT ABOVE]
        case 0x1e5b: // ṛ	[LATIN SMALL LETTER R WITH DOT BELOW]
        case 0x1e5d: // ṝ	[LATIN SMALL LETTER R WITH DOT BELOW AND MACRON]
        case 0x1e5f: // ṟ	[LATIN SMALL LETTER R WITH LINE BELOW]
        case 0x24e1: // ⓡ	[CIRCLED LATIN SMALL LETTER R]
        case 0xa75b: // ꝛ	[LATIN SMALL LETTER R ROTUNDA]
        case 0xa783: // ꞃ	[LATIN SMALL LETTER INSULAR R]
        case 0xff52: // ｒ	[FULLWIDTH LATIN SMALL LETTER R]
            outString += 'r';
            break;
        case 0x24ad: // ⒭	[PARENTHESIZED LATIN SMALL LETTER R]
            outString += '(';
            outString += 'r';
            outString += ')';
            break;
        case 0x15a: // Ś	[LATIN CAPITAL LETTER S WITH ACUTE]
        case 0x15c: // Ŝ	[LATIN CAPITAL LETTER S WITH CIRCUMFLEX]
        case 0x15e: // Ş	[LATIN CAPITAL LETTER S WITH CEDILLA]
        case 0x160: // Š	[LATIN CAPITAL LETTER S WITH CARON]
        case 0x218: // Ș	[LATIN CAPITAL LETTER S WITH COMMA BELOW]
        case 0x1e60: // Ṡ	[LATIN CAPITAL LETTER S WITH DOT ABOVE]
        case 0x1e62: // Ṣ	[LATIN CAPITAL LETTER S WITH DOT BELOW]
        case 0x1e64: // Ṥ	[LATIN CAPITAL LETTER S WITH ACUTE AND DOT ABOVE]
        case 0x1e66: // Ṧ	[LATIN CAPITAL LETTER S WITH CARON AND DOT ABOVE]
        case 0x1e68: // Ṩ	[LATIN CAPITAL LETTER S WITH DOT BELOW AND DOT ABOVE]
        case 0x24c8: // Ⓢ	[CIRCLED LATIN CAPITAL LETTER S]
        case 0xa731: // ꜱ	[LATIN LETTER SMALL CAPITAL S]
        case 0xa785: // ꞅ	[LATIN SMALL LETTER INSULAR S]
        case 0xff33: // Ｓ	[FULLWIDTH LATIN CAPITAL LETTER S]
            outString += 'S';
            break;
        case 0x15b: // ś	[LATIN SMALL LETTER S WITH ACUTE]
        case 0x15d: // ŝ	[LATIN SMALL LETTER S WITH CIRCUMFLEX]
        case 0x15f: // ş	[LATIN SMALL LETTER S WITH CEDILLA]
        case 0x161: // š	[LATIN SMALL LETTER S WITH CARON]
        case 0x17f: // ſ	http;//en.wikipedia.org/wiki/Long_S	[LATIN SMALL LETTER LONG S]
        case 0x219: // ș	[LATIN SMALL LETTER S WITH COMMA BELOW]
        case 0x23f: // ȿ	[LATIN SMALL LETTER S WITH SWASH TAIL]
        case 0x282: // ʂ	[LATIN SMALL LETTER S WITH HOOK]
        case 0x1d74: // ᵴ	[LATIN SMALL LETTER S WITH MIDDLE TILDE]
        case 0x1d8a: // ᶊ	[LATIN SMALL LETTER S WITH PALATAL HOOK]
        case 0x1e61: // ṡ	[LATIN SMALL LETTER S WITH DOT ABOVE]
        case 0x1e63: // ṣ	[LATIN SMALL LETTER S WITH DOT BELOW]
        case 0x1e65: // ṥ	[LATIN SMALL LETTER S WITH ACUTE AND DOT ABOVE]
        case 0x1e67: // ṧ	[LATIN SMALL LETTER S WITH CARON AND DOT ABOVE]
        case 0x1e69: // ṩ	[LATIN SMALL LETTER S WITH DOT BELOW AND DOT ABOVE]
        case 0x1e9c: // ẜ	[LATIN SMALL LETTER LONG S WITH DIAGONAL STROKE]
        case 0x1e9d: // ẝ	[LATIN SMALL LETTER LONG S WITH HIGH STROKE]
        case 0x24e2: // ⓢ	[CIRCLED LATIN SMALL LETTER S]
        case 0xa784: // Ꞅ	[LATIN CAPITAL LETTER INSULAR S]
        case 0xff53: // ｓ	[FULLWIDTH LATIN SMALL LETTER S]
            outString += 's';
            break;
        case 0x1e9e: // ẞ	[LATIN CAPITAL LETTER SHARP S]
            outString += 'S';
            outString += 'S';
            break;
        case 0x24ae: // ⒮	[PARENTHESIZED LATIN SMALL LETTER S]
            outString += '(';
            outString += 's';
            outString += ')';
            break;
        case 0xdf: // ß	[LATIN SMALL LETTER SHARP S]
            outString += 's';
            outString += 's';
            break;
        case 0xfb06: // ﬆ	[LATIN SMALL LIGATURE ST]
            outString += 's';
            outString += 't';
            break;
        case 0x162: // Ţ	[LATIN CAPITAL LETTER T WITH CEDILLA]
        case 0x164: // Ť	[LATIN CAPITAL LETTER T WITH CARON]
        case 0x166: // Ŧ	[LATIN CAPITAL LETTER T WITH STROKE]
        case 0x1ac: // Ƭ	[LATIN CAPITAL LETTER T WITH HOOK]
        case 0x1ae: // Ʈ	[LATIN CAPITAL LETTER T WITH RETROFLEX HOOK]
        case 0x21a: // Ț	[LATIN CAPITAL LETTER T WITH COMMA BELOW]
        case 0x23e: // Ⱦ	[LATIN CAPITAL LETTER T WITH DIAGONAL STROKE]
        case 0x1d1b: // ᴛ	[LATIN LETTER SMALL CAPITAL T]
        case 0x1e6a: // Ṫ	[LATIN CAPITAL LETTER T WITH DOT ABOVE]
        case 0x1e6c: // Ṭ	[LATIN CAPITAL LETTER T WITH DOT BELOW]
        case 0x1e6e: // Ṯ	[LATIN CAPITAL LETTER T WITH LINE BELOW]
        case 0x1e70: // Ṱ	[LATIN CAPITAL LETTER T WITH CIRCUMFLEX BELOW]
        case 0x24c9: // Ⓣ	[CIRCLED LATIN CAPITAL LETTER T]
        case 0xa786: // Ꞇ	[LATIN CAPITAL LETTER INSULAR T]
        case 0xff34: // Ｔ	[FULLWIDTH LATIN CAPITAL LETTER T]
            outString += 'T';
            break;
        case 0x163: // ţ	[LATIN SMALL LETTER T WITH CEDILLA]
        case 0x165: // ť	[LATIN SMALL LETTER T WITH CARON]
        case 0x167: // ŧ	[LATIN SMALL LETTER T WITH STROKE]
        case 0x1ab: // ƫ	[LATIN SMALL LETTER T WITH PALATAL HOOK]
        case 0x1ad: // ƭ	[LATIN SMALL LETTER T WITH HOOK]
        case 0x21b: // ț	[LATIN SMALL LETTER T WITH COMMA BELOW]
        case 0x236: // ȶ	[LATIN SMALL LETTER T WITH CURL]
        case 0x287: // ʇ	[LATIN SMALL LETTER TURNED T]
        case 0x288: // ʈ	[LATIN SMALL LETTER T WITH RETROFLEX HOOK]
        case 0x1d75: // ᵵ	[LATIN SMALL LETTER T WITH MIDDLE TILDE]
        case 0x1e6b: // ṫ	[LATIN SMALL LETTER T WITH DOT ABOVE]
        case 0x1e6d: // ṭ	[LATIN SMALL LETTER T WITH DOT BELOW]
        case 0x1e6f: // ṯ	[LATIN SMALL LETTER T WITH LINE BELOW]
        case 0x1e71: // ṱ	[LATIN SMALL LETTER T WITH CIRCUMFLEX BELOW]
        case 0x1e97: // ẗ	[LATIN SMALL LETTER T WITH DIAERESIS]
        case 0x24e3: // ⓣ	[CIRCLED LATIN SMALL LETTER T]
        case 0x2c66: // ⱦ	[LATIN SMALL LETTER T WITH DIAGONAL STROKE]
        case 0xff54: // ｔ	[FULLWIDTH LATIN SMALL LETTER T]
            outString += 't';
            break;
        case 0xde: // Þ	[LATIN CAPITAL LETTER THORN]
        case 0xa766: // Ꝧ	[LATIN CAPITAL LETTER THORN WITH STROKE THROUGH DESCENDER]
            outString += 'T';
            outString += 'H';
            break;
        case 0xa728: // Ꜩ	[LATIN CAPITAL LETTER TZ]
            outString += 'T';
            outString += 'Z';
            break;
        case 0x24af: // ⒯	[PARENTHESIZED LATIN SMALL LETTER T]
            outString += '(';
            outString += 't';
            outString += ')';
            break;
        case 0x2a8: // ʨ	[LATIN SMALL LETTER TC DIGRAPH WITH CURL]
            outString += 't';
            outString += 'c';
            break;
        case 0xfe: // þ	[LATIN SMALL LETTER THORN]
        case 0x1d7a: // ᵺ	[LATIN SMALL LETTER TH WITH STRIKETHROUGH]
        case 0xa767: // ꝧ	[LATIN SMALL LETTER THORN WITH STROKE THROUGH DESCENDER]
            outString += 't';
            outString += 'h';
            break;
        case 0x2a6: // ʦ	[LATIN SMALL LETTER TS DIGRAPH]
            outString += 't';
            outString += 's';
            break;
        case 0xa729: // ꜩ	[LATIN SMALL LETTER TZ]
            outString += 't';
            outString += 'z';
            break;
        case 0xd9: // Ù	[LATIN CAPITAL LETTER U WITH GRAVE]
        case 0xda: // Ú	[LATIN CAPITAL LETTER U WITH ACUTE]
        case 0xdb: // Û	[LATIN CAPITAL LETTER U WITH CIRCUMFLEX]
        case 0xdc: // Ü	[LATIN CAPITAL LETTER U WITH DIAERESIS]
        case 0x168: // Ũ	[LATIN CAPITAL LETTER U WITH TILDE]
        case 0x16a: // Ū	[LATIN CAPITAL LETTER U WITH MACRON]
        case 0x16c: // Ŭ	[LATIN CAPITAL LETTER U WITH BREVE]
        case 0x16e: // Ů	[LATIN CAPITAL LETTER U WITH RING ABOVE]
        case 0x170: // Ű	[LATIN CAPITAL LETTER U WITH DOUBLE ACUTE]
        case 0x172: // Ų	[LATIN CAPITAL LETTER U WITH OGONEK]
        case 0x1af: // Ư	[LATIN CAPITAL LETTER U WITH HORN]
        case 0x1d3: // Ǔ	[LATIN CAPITAL LETTER U WITH CARON]
        case 0x1d5: // Ǖ	[LATIN CAPITAL LETTER U WITH DIAERESIS AND MACRON]
        case 0x1d7: // Ǘ	[LATIN CAPITAL LETTER U WITH DIAERESIS AND ACUTE]
        case 0x1d9: // Ǚ	[LATIN CAPITAL LETTER U WITH DIAERESIS AND CARON]
        case 0x1db: // Ǜ	[LATIN CAPITAL LETTER U WITH DIAERESIS AND GRAVE]
        case 0x214: // Ȕ	[LATIN CAPITAL LETTER U WITH DOUBLE GRAVE]
        case 0x216: // Ȗ	[LATIN CAPITAL LETTER U WITH INVERTED BREVE]
        case 0x244: // Ʉ	[LATIN CAPITAL LETTER U BAR]
        case 0x1d1c: // ᴜ	[LATIN LETTER SMALL CAPITAL U]
        case 0x1d7e: // ᵾ	[LATIN SMALL CAPITAL LETTER U WITH STROKE]
        case 0x1e72: // Ṳ	[LATIN CAPITAL LETTER U WITH DIAERESIS BELOW]
        case 0x1e74: // Ṵ	[LATIN CAPITAL LETTER U WITH TILDE BELOW]
        case 0x1e76: // Ṷ	[LATIN CAPITAL LETTER U WITH CIRCUMFLEX BELOW]
        case 0x1e78: // Ṹ	[LATIN CAPITAL LETTER U WITH TILDE AND ACUTE]
        case 0x1e7a: // Ṻ	[LATIN CAPITAL LETTER U WITH MACRON AND DIAERESIS]
        case 0x1ee4: // Ụ	[LATIN CAPITAL LETTER U WITH DOT BELOW]
        case 0x1ee6: // Ủ	[LATIN CAPITAL LETTER U WITH HOOK ABOVE]
        case 0x1ee8: // Ứ	[LATIN CAPITAL LETTER U WITH HORN AND ACUTE]
        case 0x1eea: // Ừ	[LATIN CAPITAL LETTER U WITH HORN AND GRAVE]
        case 0x1eec: // Ử	[LATIN CAPITAL LETTER U WITH HORN AND HOOK ABOVE]
        case 0x1eee: // Ữ	[LATIN CAPITAL LETTER U WITH HORN AND TILDE]
        case 0x1ef0: // Ự	[LATIN CAPITAL LETTER U WITH HORN AND DOT BELOW]
        case 0x24ca: // Ⓤ	[CIRCLED LATIN CAPITAL LETTER U]
        case 0xff35: // Ｕ	[FULLWIDTH LATIN CAPITAL LETTER U]
            outString += 'U';
            break;
        case 0xf9: // ù	[LATIN SMALL LETTER U WITH GRAVE]
        case 0xfa: // ú	[LATIN SMALL LETTER U WITH ACUTE]
        case 0xfb: // û	[LATIN SMALL LETTER U WITH CIRCUMFLEX]
        case 0xfc: // ü	[LATIN SMALL LETTER U WITH DIAERESIS]
        case 0x169: // ũ	[LATIN SMALL LETTER U WITH TILDE]
        case 0x16b: // ū	[LATIN SMALL LETTER U WITH MACRON]
        case 0x16d: // ŭ	[LATIN SMALL LETTER U WITH BREVE]
        case 0x16f: // ů	[LATIN SMALL LETTER U WITH RING ABOVE]
        case 0x171: // ű	[LATIN SMALL LETTER U WITH DOUBLE ACUTE]
        case 0x173: // ų	[LATIN SMALL LETTER U WITH OGONEK]
        case 0x1b0: // ư	[LATIN SMALL LETTER U WITH HORN]
        case 0x1d4: // ǔ	[LATIN SMALL LETTER U WITH CARON]
        case 0x1d6: // ǖ	[LATIN SMALL LETTER U WITH DIAERESIS AND MACRON]
        case 0x1d8: // ǘ	[LATIN SMALL LETTER U WITH DIAERESIS AND ACUTE]
        case 0x1da: // ǚ	[LATIN SMALL LETTER U WITH DIAERESIS AND CARON]
        case 0x1dc: // ǜ	[LATIN SMALL LETTER U WITH DIAERESIS AND GRAVE]
        case 0x215: // ȕ	[LATIN SMALL LETTER U WITH DOUBLE GRAVE]
        case 0x217: // ȗ	[LATIN SMALL LETTER U WITH INVERTED BREVE]
        case 0x289: // ʉ	[LATIN SMALL LETTER U BAR]
        case 0x1d64: // ᵤ	[LATIN SUBSCRIPT SMALL LETTER U]
        case 0x1d99: // ᶙ	[LATIN SMALL LETTER U WITH RETROFLEX HOOK]
        case 0x1e73: // ṳ	[LATIN SMALL LETTER U WITH DIAERESIS BELOW]
        case 0x1e75: // ṵ	[LATIN SMALL LETTER U WITH TILDE BELOW]
        case 0x1e77: // ṷ	[LATIN SMALL LETTER U WITH CIRCUMFLEX BELOW]
        case 0x1e79: // ṹ	[LATIN SMALL LETTER U WITH TILDE AND ACUTE]
        case 0x1e7b: // ṻ	[LATIN SMALL LETTER U WITH MACRON AND DIAERESIS]
        case 0x1ee5: // ụ	[LATIN SMALL LETTER U WITH DOT BELOW]
        case 0x1ee7: // ủ	[LATIN SMALL LETTER U WITH HOOK ABOVE]
        case 0x1ee9: // ứ	[LATIN SMALL LETTER U WITH HORN AND ACUTE]
        case 0x1eeb: // ừ	[LATIN SMALL LETTER U WITH HORN AND GRAVE]
        case 0x1eed: // ử	[LATIN SMALL LETTER U WITH HORN AND HOOK ABOVE]
        case 0x1eef: // ữ	[LATIN SMALL LETTER U WITH HORN AND TILDE]
        case 0x1ef1: // ự	[LATIN SMALL LETTER U WITH HORN AND DOT BELOW]
        case 0x24e4: // ⓤ	[CIRCLED LATIN SMALL LETTER U]
        case 0xff55: // ｕ	[FULLWIDTH LATIN SMALL LETTER U]
            outString += 'u';
            break;
        case 0x24b0: // ⒰	[PARENTHESIZED LATIN SMALL LETTER U]
            outString += '(';
            outString += 'u';
            outString += ')';
            break;
        case 0x1d6b: // ᵫ	[LATIN SMALL LETTER UE]
            outString += 'u';
            outString += 'e';
            break;
        case 0x1b2: // Ʋ	[LATIN CAPITAL LETTER V WITH HOOK]
        case 0x245: // Ʌ	[LATIN CAPITAL LETTER TURNED V]
        case 0x1d20: // ᴠ	[LATIN LETTER SMALL CAPITAL V]
        case 0x1e7c: // Ṽ	[LATIN CAPITAL LETTER V WITH TILDE]
        case 0x1e7e: // Ṿ	[LATIN CAPITAL LETTER V WITH DOT BELOW]
        case 0x1efc: // Ỽ	[LATIN CAPITAL LETTER MIDDLE-WELSH V]
        case 0x24cb: // Ⓥ	[CIRCLED LATIN CAPITAL LETTER V]
        case 0xa75e: // Ꝟ	[LATIN CAPITAL LETTER V WITH DIAGONAL STROKE]
        case 0xa768: // Ꝩ	[LATIN CAPITAL LETTER VEND]
        case 0xff36: // Ｖ	[FULLWIDTH LATIN CAPITAL LETTER V]
            outString += 'V';
            break;
        case 0x28b: // ʋ	[LATIN SMALL LETTER V WITH HOOK]
        case 0x28c: // ʌ	[LATIN SMALL LETTER TURNED V]
        case 0x1d65: // ᵥ	[LATIN SUBSCRIPT SMALL LETTER V]
        case 0x1d8c: // ᶌ	[LATIN SMALL LETTER V WITH PALATAL HOOK]
        case 0x1e7d: // ṽ	[LATIN SMALL LETTER V WITH TILDE]
        case 0x1e7f: // ṿ	[LATIN SMALL LETTER V WITH DOT BELOW]
        case 0x24e5: // ⓥ	[CIRCLED LATIN SMALL LETTER V]
        case 0x2c71: // ⱱ	[LATIN SMALL LETTER V WITH RIGHT HOOK]
        case 0x2c74: // ⱴ	[LATIN SMALL LETTER V WITH CURL]
        case 0xa75f: // ꝟ	[LATIN SMALL LETTER V WITH DIAGONAL STROKE]
        case 0xff56: // ｖ	[FULLWIDTH LATIN SMALL LETTER V]
            outString += 'v';
            break;
        case 0xa760: // Ꝡ	[LATIN CAPITAL LETTER VY]
            outString += 'V';
            outString += 'Y';
            break;
        case 0x24b1: // ⒱	[PARENTHESIZED LATIN SMALL LETTER V]
            outString += '(';
            outString += 'v';
            outString += ')';
            break;
        case 0xa761: // ꝡ	[LATIN SMALL LETTER VY]
            outString += 'v';
            outString += 'y';
            break;
        case 0x174: // Ŵ	[LATIN CAPITAL LETTER W WITH CIRCUMFLEX]
        case 0x1f7: // Ƿ	http;//en.wikipedia.org/wiki/Wynn	[LATIN CAPITAL LETTER WYNN]
        case 0x1d21: // ᴡ	[LATIN LETTER SMALL CAPITAL W]
        case 0x1e80: // Ẁ	[LATIN CAPITAL LETTER W WITH GRAVE]
        case 0x1e82: // Ẃ	[LATIN CAPITAL LETTER W WITH ACUTE]
        case 0x1e84: // Ẅ	[LATIN CAPITAL LETTER W WITH DIAERESIS]
        case 0x1e86: // Ẇ	[LATIN CAPITAL LETTER W WITH DOT ABOVE]
        case 0x1e88: // Ẉ	[LATIN CAPITAL LETTER W WITH DOT BELOW]
        case 0x24cc: // Ⓦ	[CIRCLED LATIN CAPITAL LETTER W]
        case 0x2c72: // Ⱳ	[LATIN CAPITAL LETTER W WITH HOOK]
        case 0xff37: // Ｗ	[FULLWIDTH LATIN CAPITAL LETTER W]
            outString += 'W';
            break;
        case 0x175: // ŵ	[LATIN SMALL LETTER W WITH CIRCUMFLEX]
        case 0x1bf: // ƿ	http;//en.wikipedia.org/wiki/Wynn	[LATIN LETTER WYNN]
        case 0x28d: // ʍ	[LATIN SMALL LETTER TURNED W]
        case 0x1e81: // ẁ	[LATIN SMALL LETTER W WITH GRAVE]
        case 0x1e83: // ẃ	[LATIN SMALL LETTER W WITH ACUTE]
        case 0x1e85: // ẅ	[LATIN SMALL LETTER W WITH DIAERESIS]
        case 0x1e87: // ẇ	[LATIN SMALL LETTER W WITH DOT ABOVE]
        case 0x1e89: // ẉ	[LATIN SMALL LETTER W WITH DOT BELOW]
        case 0x1e98: // ẘ	[LATIN SMALL LETTER W WITH RING ABOVE]
        case 0x24e6: // ⓦ	[CIRCLED LATIN SMALL LETTER W]
        case 0x2c73: // ⱳ	[LATIN SMALL LETTER W WITH HOOK]
        case 0xff57: // ｗ	[FULLWIDTH LATIN SMALL LETTER W]
            outString += 'w';
            break;
        case 0x24b2: // ⒲	[PARENTHESIZED LATIN SMALL LETTER W]
            outString += '(';
            outString += 'w';
            outString += ')';
            break;
        case 0x1e8a: // Ẋ	[LATIN CAPITAL LETTER X WITH DOT ABOVE]
        case 0x1e8c: // Ẍ	[LATIN CAPITAL LETTER X WITH DIAERESIS]
        case 0x24cd: // Ⓧ	[CIRCLED LATIN CAPITAL LETTER X]
        case 0xff38: // Ｘ	[FULLWIDTH LATIN CAPITAL LETTER X]
            outString += 'X';
            break;
        case 0x1d8d: // ᶍ	[LATIN SMALL LETTER X WITH PALATAL HOOK]
        case 0x1e8b: // ẋ	[LATIN SMALL LETTER X WITH DOT ABOVE]
        case 0x1e8d: // ẍ	[LATIN SMALL LETTER X WITH DIAERESIS]
        case 0x2093: // ₓ	[LATIN SUBSCRIPT SMALL LETTER X]
        case 0x24e7: // ⓧ	[CIRCLED LATIN SMALL LETTER X]
        case 0xff58: // ｘ	[FULLWIDTH LATIN SMALL LETTER X]
            outString += 'x';
            break;
        case 0x24b3: // ⒳	[PARENTHESIZED LATIN SMALL LETTER X]
            outString += '(';
            outString += 'x';
            outString += ')';
            break;
        case 0xdd: // Ý	[LATIN CAPITAL LETTER Y WITH ACUTE]
        case 0x176: // Ŷ	[LATIN CAPITAL LETTER Y WITH CIRCUMFLEX]
        case 0x178: // Ÿ	[LATIN CAPITAL LETTER Y WITH DIAERESIS]
        case 0x1b3: // Ƴ	[LATIN CAPITAL LETTER Y WITH HOOK]
        case 0x232: // Ȳ	[LATIN CAPITAL LETTER Y WITH MACRON]
        case 0x24e: // Ɏ	[LATIN CAPITAL LETTER Y WITH STROKE]
        case 0x28f: // ʏ	[LATIN LETTER SMALL CAPITAL Y]
        case 0x1e8e: // Ẏ	[LATIN CAPITAL LETTER Y WITH DOT ABOVE]
        case 0x1ef2: // Ỳ	[LATIN CAPITAL LETTER Y WITH GRAVE]
        case 0x1ef4: // Ỵ	[LATIN CAPITAL LETTER Y WITH DOT BELOW]
        case 0x1ef6: // Ỷ	[LATIN CAPITAL LETTER Y WITH HOOK ABOVE]
        case 0x1ef8: // Ỹ	[LATIN CAPITAL LETTER Y WITH TILDE]
        case 0x1efe: // Ỿ	[LATIN CAPITAL LETTER Y WITH LOOP]
        case 0x24ce: // Ⓨ	[CIRCLED LATIN CAPITAL LETTER Y]
        case 0xff39: // Ｙ	[FULLWIDTH LATIN CAPITAL LETTER Y]
            outString += 'Y';
            break;
        case 0xfd: // ý	[LATIN SMALL LETTER Y WITH ACUTE]
        case 0xff: // ÿ	[LATIN SMALL LETTER Y WITH DIAERESIS]
        case 0x177: // ŷ	[LATIN SMALL LETTER Y WITH CIRCUMFLEX]
        case 0x1b4: // ƴ	[LATIN SMALL LETTER Y WITH HOOK]
        case 0x233: // ȳ	[LATIN SMALL LETTER Y WITH MACRON]
        case 0x24f: // ɏ	[LATIN SMALL LETTER Y WITH STROKE]
        case 0x28e: // ʎ	[LATIN SMALL LETTER TURNED Y]
        case 0x1e8f: // ẏ	[LATIN SMALL LETTER Y WITH DOT ABOVE]
        case 0x1e99: // ẙ	[LATIN SMALL LETTER Y WITH RING ABOVE]
        case 0x1ef3: // ỳ	[LATIN SMALL LETTER Y WITH GRAVE]
        case 0x1ef5: // ỵ	[LATIN SMALL LETTER Y WITH DOT BELOW]
        case 0x1ef7: // ỷ	[LATIN SMALL LETTER Y WITH HOOK ABOVE]
        case 0x1ef9: // ỹ	[LATIN SMALL LETTER Y WITH TILDE]
        case 0x1eff: // ỿ	[LATIN SMALL LETTER Y WITH LOOP]
        case 0x24e8: // ⓨ	[CIRCLED LATIN SMALL LETTER Y]
        case 0xff59: // ｙ	[FULLWIDTH LATIN SMALL LETTER Y]
            outString += 'y';
            break;
        case 0x24b4: // ⒴	[PARENTHESIZED LATIN SMALL LETTER Y]
            outString += '(';
            outString += 'y';
            outString += ')';
            break;
        case 0x179: // Ź	[LATIN CAPITAL LETTER Z WITH ACUTE]
        case 0x17b: // Ż	[LATIN CAPITAL LETTER Z WITH DOT ABOVE]
        case 0x17d: // Ž	[LATIN CAPITAL LETTER Z WITH CARON]
        case 0x1b5: // Ƶ	[LATIN CAPITAL LETTER Z WITH STROKE]
        case 0x21c: // Ȝ	http;//en.wikipedia.org/wiki/Yogh	[LATIN CAPITAL LETTER YOGH]
        case 0x224: // Ȥ	[LATIN CAPITAL LETTER Z WITH HOOK]
        case 0x1d22: // ᴢ	[LATIN LETTER SMALL CAPITAL Z]
        case 0x1e90: // Ẑ	[LATIN CAPITAL LETTER Z WITH CIRCUMFLEX]
        case 0x1e92: // Ẓ	[LATIN CAPITAL LETTER Z WITH DOT BELOW]
        case 0x1e94: // Ẕ	[LATIN CAPITAL LETTER Z WITH LINE BELOW]
        case 0x24cf: // Ⓩ	[CIRCLED LATIN CAPITAL LETTER Z]
        case 0x2c6b: // Ⱬ	[LATIN CAPITAL LETTER Z WITH DESCENDER]
        case 0xa762: // Ꝣ	[LATIN CAPITAL LETTER VISIGOTHIC Z]
        case 0xff3a: // Ｚ	[FULLWIDTH LATIN CAPITAL LETTER Z]
            outString += 'Z';
            break;
        case 0x17a: // ź	[LATIN SMALL LETTER Z WITH ACUTE]
        case 0x17c: // ż	[LATIN SMALL LETTER Z WITH DOT ABOVE]
        case 0x17e: // ž	[LATIN SMALL LETTER Z WITH CARON]
        case 0x1b6: // ƶ	[LATIN SMALL LETTER Z WITH STROKE]
        case 0x21d: // ȝ	http;//en.wikipedia.org/wiki/Yogh	[LATIN SMALL LETTER YOGH]
        case 0x225: // ȥ	[LATIN SMALL LETTER Z WITH HOOK]
        case 0x240: // ɀ	[LATIN SMALL LETTER Z WITH SWASH TAIL]
        case 0x290: // ʐ	[LATIN SMALL LETTER Z WITH RETROFLEX HOOK]
        case 0x291: // ʑ	[LATIN SMALL LETTER Z WITH CURL]
        case 0x1d76: // ᵶ	[LATIN SMALL LETTER Z WITH MIDDLE TILDE]
        case 0x1d8e: // ᶎ	[LATIN SMALL LETTER Z WITH PALATAL HOOK]
        case 0x1e91: // ẑ	[LATIN SMALL LETTER Z WITH CIRCUMFLEX]
        case 0x1e93: // ẓ	[LATIN SMALL LETTER Z WITH DOT BELOW]
        case 0x1e95: // ẕ	[LATIN SMALL LETTER Z WITH LINE BELOW]
        case 0x24e9: // ⓩ	[CIRCLED LATIN SMALL LETTER Z]
        case 0x2c6c: // ⱬ	[LATIN SMALL LETTER Z WITH DESCENDER]
        case 0xa763: // ꝣ	[LATIN SMALL LETTER VISIGOTHIC Z]
        case 0xff5a: // ｚ	[FULLWIDTH LATIN SMALL LETTER Z]
            outString += 'z';
            break;
        case 0x24b5: // ⒵	[PARENTHESIZED LATIN SMALL LETTER Z]
            outString += '(';
            outString += 'z';
            outString += ')';
            break;
        case 0x2070: // ⁰	[SUPERSCRIPT ZERO]
        case 0x2080: // ₀	[SUBSCRIPT ZERO]
        case 0x24ea: // ⓪	[CIRCLED DIGIT ZERO]
        case 0x24ff: // ⓿	[NEGATIVE CIRCLED DIGIT ZERO]
        case 0xff10: // ０	[FULLWIDTH DIGIT ZERO]
            outString += '0';
            break;
        case 0xb9: // ¹	[SUPERSCRIPT ONE]
        case 0x2081: // ₁	[SUBSCRIPT ONE]
        case 0x2460: // ①	[CIRCLED DIGIT ONE]
        case 0x24f5: // ⓵	[DOUBLE CIRCLED DIGIT ONE]
        case 0x2776: // ❶	[DINGBAT NEGATIVE CIRCLED DIGIT ONE]
        case 0x2780: // ➀	[DINGBAT CIRCLED SANS-SERIF DIGIT ONE]
        case 0x278a: // ➊	[DINGBAT NEGATIVE CIRCLED SANS-SERIF DIGIT ONE]
        case 0xff11: // １	[FULLWIDTH DIGIT ONE]
            outString += '1';
            break;
        case 0x2488: // ⒈	[DIGIT ONE FULL STOP]
            outString += '1';
            outString += '.';
            break;
        case 0x2474: // ⑴	[PARENTHESIZED DIGIT ONE]
            outString += '(';
            outString += '1';
            outString += ')';
            break;
        case 0xb2: // ²	[SUPERSCRIPT TWO]
        case 0x2082: // ₂	[SUBSCRIPT TWO]
        case 0x2461: // ②	[CIRCLED DIGIT TWO]
        case 0x24f6: // ⓶	[DOUBLE CIRCLED DIGIT TWO]
        case 0x2777: // ❷	[DINGBAT NEGATIVE CIRCLED DIGIT TWO]
        case 0x2781: // ➁	[DINGBAT CIRCLED SANS-SERIF DIGIT TWO]
        case 0x278b: // ➋	[DINGBAT NEGATIVE CIRCLED SANS-SERIF DIGIT TWO]
        case 0xff12: // ２	[FULLWIDTH DIGIT TWO]
            outString += '2';
            break;
        case 0x2489: // ⒉	[DIGIT TWO FULL STOP]
            outString += '2';
            outString += '.';
            break;
        case 0x2475: // ⑵	[PARENTHESIZED DIGIT TWO]
            outString += '(';
            outString += '2';
            outString += ')';
            break;
        case 0xb3: // ³	[SUPERSCRIPT THREE]
        case 0x2083: // ₃	[SUBSCRIPT THREE]
        case 0x2462: // ③	[CIRCLED DIGIT THREE]
        case 0x24f7: // ⓷	[DOUBLE CIRCLED DIGIT THREE]
        case 0x2778: // ❸	[DINGBAT NEGATIVE CIRCLED DIGIT THREE]
        case 0x2782: // ➂	[DINGBAT CIRCLED SANS-SERIF DIGIT THREE]
        case 0x278c: // ➌	[DINGBAT NEGATIVE CIRCLED SANS-SERIF DIGIT THREE]
        case 0xff13: // ３	[FULLWIDTH DIGIT THREE]
            outString += '3';
            break;
        case 0x248a: // ⒊	[DIGIT THREE FULL STOP]
            outString += '3';
            outString += '.';
            break;
        case 0x2476: // ⑶	[PARENTHESIZED DIGIT THREE]
            outString += '(';
            outString += '3';
            outString += ')';
            break;
        case 0x2074: // ⁴	[SUPERSCRIPT FOUR]
        case 0x2084: // ₄	[SUBSCRIPT FOUR]
        case 0x2463: // ④	[CIRCLED DIGIT FOUR]
        case 0x24f8: // ⓸	[DOUBLE CIRCLED DIGIT FOUR]
        case 0x2779: // ❹	[DINGBAT NEGATIVE CIRCLED DIGIT FOUR]
        case 0x2783: // ➃	[DINGBAT CIRCLED SANS-SERIF DIGIT FOUR]
        case 0x278d: // ➍	[DINGBAT NEGATIVE CIRCLED SANS-SERIF DIGIT FOUR]
        case 0xff14: // ４	[FULLWIDTH DIGIT FOUR]
            outString += '4';
            break;
        case 0x248b: // ⒋	[DIGIT FOUR FULL STOP]
            outString += '4';
            outString += '.';
            break;
        case 0x2477: // ⑷	[PARENTHESIZED DIGIT FOUR]
            outString += '(';
            outString += '4';
            outString += ')';
            break;
        case 0x2075: // ⁵	[SUPERSCRIPT FIVE]
        case 0x2085: // ₅	[SUBSCRIPT FIVE]
        case 0x2464: // ⑤	[CIRCLED DIGIT FIVE]
        case 0x24f9: // ⓹	[DOUBLE CIRCLED DIGIT FIVE]
        case 0x277a: // ❺	[DINGBAT NEGATIVE CIRCLED DIGIT FIVE]
        case 0x2784: // ➄	[DINGBAT CIRCLED SANS-SERIF DIGIT FIVE]
        case 0x278e: // ➎	[DINGBAT NEGATIVE CIRCLED SANS-SERIF DIGIT FIVE]
        case 0xff15: // ５	[FULLWIDTH DIGIT FIVE]
            outString += '5';
            break;
        case 0x248c: // ⒌	[DIGIT FIVE FULL STOP]
            outString += '5';
            outString += '.';
            break;
        case 0x2478: // ⑸	[PARENTHESIZED DIGIT FIVE]
            outString += '(';
            outString += '5';
            outString += ')';
            break;
        case 0x2076: // ⁶	[SUPERSCRIPT SIX]
        case 0x2086: // ₆	[SUBSCRIPT SIX]
        case 0x2465: // ⑥	[CIRCLED DIGIT SIX]
        case 0x24fa: // ⓺	[DOUBLE CIRCLED DIGIT SIX]
        case 0x277b: // ❻	[DINGBAT NEGATIVE CIRCLED DIGIT SIX]
        case 0x2785: // ➅	[DINGBAT CIRCLED SANS-SERIF DIGIT SIX]
        case 0x278f: // ➏	[DINGBAT NEGATIVE CIRCLED SANS-SERIF DIGIT SIX]
        case 0xff16: // ６	[FULLWIDTH DIGIT SIX]
            outString += '6';
            break;
        case 0x248d: // ⒍	[DIGIT SIX FULL STOP]
            outString += '6';
            outString += '.';
            break;
        case 0x2479: // ⑹	[PARENTHESIZED DIGIT SIX]
            outString += '(';
            outString += '6';
            outString += ')';
            break;
        case 0x2077: // ⁷	[SUPERSCRIPT SEVEN]
        case 0x2087: // ₇	[SUBSCRIPT SEVEN]
        case 0x2466: // ⑦	[CIRCLED DIGIT SEVEN]
        case 0x24fb: // ⓻	[DOUBLE CIRCLED DIGIT SEVEN]
        case 0x277c: // ❼	[DINGBAT NEGATIVE CIRCLED DIGIT SEVEN]
        case 0x2786: // ➆	[DINGBAT CIRCLED SANS-SERIF DIGIT SEVEN]
        case 0x2790: // ➐	[DINGBAT NEGATIVE CIRCLED SANS-SERIF DIGIT SEVEN]
        case 0xff17: // ７	[FULLWIDTH DIGIT SEVEN]
            outString += '7';
            break;
        case 0x248e: // ⒎	[DIGIT SEVEN FULL STOP]
            outString += '7';
            outString += '.';
            break;
        case 0x247a: // ⑺	[PARENTHESIZED DIGIT SEVEN]
            outString += '(';
            outString += '7';
            outString += ')';
            break;
        case 0x2078: // ⁸	[SUPERSCRIPT EIGHT]
        case 0x2088: // ₈	[SUBSCRIPT EIGHT]
        case 0x2467: // ⑧	[CIRCLED DIGIT EIGHT]
        case 0x24fc: // ⓼	[DOUBLE CIRCLED DIGIT EIGHT]
        case 0x277d: // ❽	[DINGBAT NEGATIVE CIRCLED DIGIT EIGHT]
        case 0x2787: // ➇	[DINGBAT CIRCLED SANS-SERIF DIGIT EIGHT]
        case 0x2791: // ➑	[DINGBAT NEGATIVE CIRCLED SANS-SERIF DIGIT EIGHT]
        case 0xff18: // ８	[FULLWIDTH DIGIT EIGHT]
            outString += '8';
            break;
        case 0x248f: // ⒏	[DIGIT EIGHT FULL STOP]
            outString += '8';
            outString += '.';
            break;
        case 0x247b: // ⑻	[PARENTHESIZED DIGIT EIGHT]
            outString += '(';
            outString += '8';
            outString += ')';
            break;
        case 0x2079: // ⁹	[SUPERSCRIPT NINE]
        case 0x2089: // ₉	[SUBSCRIPT NINE]
        case 0x2468: // ⑨	[CIRCLED DIGIT NINE]
        case 0x24fd: // ⓽	[DOUBLE CIRCLED DIGIT NINE]
        case 0x277e: // ❾	[DINGBAT NEGATIVE CIRCLED DIGIT NINE]
        case 0x2788: // ➈	[DINGBAT CIRCLED SANS-SERIF DIGIT NINE]
        case 0x2792: // ➒	[DINGBAT NEGATIVE CIRCLED SANS-SERIF DIGIT NINE]
        case 0xff19: // ９	[FULLWIDTH DIGIT NINE]
            outString += '9';
            break;
        case 0x2490: // ⒐	[DIGIT NINE FULL STOP]
            outString += '9';
            outString += '.';
            break;
        case 0x247c: // ⑼	[PARENTHESIZED DIGIT NINE]
            outString += '(';
            outString += '9';
            outString += ')';
            break;
        case 0x2469: // ⑩	[CIRCLED NUMBER TEN]
        case 0x24fe: // ⓾	[DOUBLE CIRCLED NUMBER TEN]
        case 0x277f: // ❿	[DINGBAT NEGATIVE CIRCLED NUMBER TEN]
        case 0x2789: // ➉	[DINGBAT CIRCLED SANS-SERIF NUMBER TEN]
        case 0x2793: // ➓	[DINGBAT NEGATIVE CIRCLED SANS-SERIF NUMBER TEN]
            outString += '1';
            outString += '0';
            break;
        case 0x2491: // ⒑	[NUMBER TEN FULL STOP]
            outString += '1';
            outString += '0';
            outString += '.';
            break;
        case 0x247d: // ⑽	[PARENTHESIZED NUMBER TEN]
            outString += '(';
            outString += '1';
            outString += '0';
            outString += ')';
            break;
        case 0x246a: // ⑪	[CIRCLED NUMBER ELEVEN]
        case 0x24eb: // ⓫	[NEGATIVE CIRCLED NUMBER ELEVEN]
            outString += '1';
            outString += '1';
            break;
        case 0x2492: // ⒒	[NUMBER ELEVEN FULL STOP]
            outString += '1';
            outString += '1';
            outString += '.';
            break;
        case 0x247e: // ⑾	[PARENTHESIZED NUMBER ELEVEN]
            outString += '(';
            outString += '1';
            outString += '1';
            outString += ')';
            break;
        case 0x246b: // ⑫	[CIRCLED NUMBER TWELVE]
        case 0x24ec: // ⓬	[NEGATIVE CIRCLED NUMBER TWELVE]
            outString += '1';
            outString += '2';
            break;
        case 0x2493: // ⒓	[NUMBER TWELVE FULL STOP]
            outString += '1';
            outString += '2';
            outString += '.';
            break;
        case 0x247f: // ⑿	[PARENTHESIZED NUMBER TWELVE]
            outString += '(';
            outString += '1';
            outString += '2';
            outString += ')';
            break;
        case 0x246c: // ⑬	[CIRCLED NUMBER THIRTEEN]
        case 0x24ed: // ⓭	[NEGATIVE CIRCLED NUMBER THIRTEEN]
            outString += '1';
            outString += '3';
            break;
        case 0x2494: // ⒔	[NUMBER THIRTEEN FULL STOP]
            outString += '1';
            outString += '3';
            outString += '.';
            break;
        case 0x2480: // ⒀	[PARENTHESIZED NUMBER THIRTEEN]
            outString += '(';
            outString += '1';
            outString += '3';
            outString += ')';
            break;
        case 0x246d: // ⑭	[CIRCLED NUMBER FOURTEEN]
        case 0x24ee: // ⓮	[NEGATIVE CIRCLED NUMBER FOURTEEN]
            outString += '1';
            outString += '4';
            break;
        case 0x2495: // ⒕	[NUMBER FOURTEEN FULL STOP]
            outString += '1';
            outString += '4';
            outString += '.';
            break;
        case 0x2481: // ⒁	[PARENTHESIZED NUMBER FOURTEEN]
            outString += '(';
            outString += '1';
            outString += '4';
            outString += ')';
            break;
        case 0x246e: // ⑮	[CIRCLED NUMBER FIFTEEN]
        case 0x24ef: // ⓯	[NEGATIVE CIRCLED NUMBER FIFTEEN]
            outString += '1';
            outString += '5';
            break;
        case 0x2496: // ⒖	[NUMBER FIFTEEN FULL STOP]
            outString += '1';
            outString += '5';
            outString += '.';
            break;
        case 0x2482: // ⒂	[PARENTHESIZED NUMBER FIFTEEN]
            outString += '(';
            outString += '1';
            outString += '5';
            outString += ')';
            break;
        case 0x246f: // ⑯	[CIRCLED NUMBER SIXTEEN]
        case 0x24f0: // ⓰	[NEGATIVE CIRCLED NUMBER SIXTEEN]
            outString += '1';
            outString += '6';
            break;
        case 0x2497: // ⒗	[NUMBER SIXTEEN FULL STOP]
            outString += '1';
            outString += '6';
            outString += '.';
            break;
        case 0x2483: // ⒃	[PARENTHESIZED NUMBER SIXTEEN]
            outString += '(';
            outString += '1';
            outString += '6';
            outString += ')';
            break;
        case 0x2470: // ⑰	[CIRCLED NUMBER SEVENTEEN]
        case 0x24f1: // ⓱	[NEGATIVE CIRCLED NUMBER SEVENTEEN]
            outString += '1';
            outString += '7';
            break;
        case 0x2498: // ⒘	[NUMBER SEVENTEEN FULL STOP]
            outString += '1';
            outString += '7';
            outString += '.';
            break;
        case 0x2484: // ⒄	[PARENTHESIZED NUMBER SEVENTEEN]
            outString += '(';
            outString += '1';
            outString += '7';
            outString += ')';
            break;
        case 0x2471: // ⑱	[CIRCLED NUMBER EIGHTEEN]
        case 0x24f2: // ⓲	[NEGATIVE CIRCLED NUMBER EIGHTEEN]
            outString += '1';
            outString += '8';
            break;
        case 0x2499: // ⒙	[NUMBER EIGHTEEN FULL STOP]
            outString += '1';
            outString += '8';
            outString += '.';
            break;
        case 0x2485: // ⒅	[PARENTHESIZED NUMBER EIGHTEEN]
            outString += '(';
            outString += '1';
            outString += '8';
            outString += ')';
            break;
        case 0x2472: // ⑲	[CIRCLED NUMBER NINETEEN]
        case 0x24f3: // ⓳	[NEGATIVE CIRCLED NUMBER NINETEEN]
            outString += '1';
            outString += '9';
            break;
        case 0x249a: // ⒚	[NUMBER NINETEEN FULL STOP]
            outString += '1';
            outString += '9';
            outString += '.';
            break;
        case 0x2486: // ⒆	[PARENTHESIZED NUMBER NINETEEN]
            outString += '(';
            outString += '1';
            outString += '9';
            outString += ')';
            break;
        case 0x2473: // ⑳	[CIRCLED NUMBER TWENTY]
        case 0x24f4: // ⓴	[NEGATIVE CIRCLED NUMBER TWENTY]
            outString += '2';
            outString += '0';
            break;
        case 0x249b: // ⒛	[NUMBER TWENTY FULL STOP]
            outString += '2';
            outString += '0';
            outString += '.';
            break;
        case 0x2487: // ⒇	[PARENTHESIZED NUMBER TWENTY]
            outString += '(';
            outString += '2';
            outString += '0';
            outString += ')';
            break;
        case 0xab: // «	[LEFT-POINTING DOUBLE ANGLE QUOTATION MARK]
        case 0xbb: // »	[RIGHT-POINTING DOUBLE ANGLE QUOTATION MARK]
        case 0x201c: // “	[LEFT DOUBLE QUOTATION MARK]
        case 0x201d: // ”	[RIGHT DOUBLE QUOTATION MARK]
        case 0x201e: // „	[DOUBLE LOW-9 QUOTATION MARK]
        case 0x2033: // ″	[DOUBLE PRIME]
        case 0x2036: // ‶	[REVERSED DOUBLE PRIME]
        case 0x275d: // ❝	[HEAVY DOUBLE TURNED COMMA QUOTATION MARK ORNAMENT]
        case 0x275e: // ❞	[HEAVY DOUBLE COMMA QUOTATION MARK ORNAMENT]
        case 0x276e: // ❮	[HEAVY LEFT-POINTING ANGLE QUOTATION MARK ORNAMENT]
        case 0x276f: // ❯	[HEAVY RIGHT-POINTING ANGLE QUOTATION MARK ORNAMENT]
        case 0xff02: // ＂	[FULLWIDTH QUOTATION MARK]
            outString += '"';
            break;
        case 0x2018: // ‘	[LEFT SINGLE QUOTATION MARK]
        case 0x2019: // ’	[RIGHT SINGLE QUOTATION MARK]
        case 0x201a: // ‚	[SINGLE LOW-9 QUOTATION MARK]
        case 0x201b: // ‛	[SINGLE HIGH-REVERSED-9 QUOTATION MARK]
        case 0x2032: // ′	[PRIME]
        case 0x2035: // ‵	[REVERSED PRIME]
        case 0x2039: // ‹	[SINGLE LEFT-POINTING ANGLE QUOTATION MARK]
        case 0x203a: // ›	[SINGLE RIGHT-POINTING ANGLE QUOTATION MARK]
        case 0x275b: // ❛	[HEAVY SINGLE TURNED COMMA QUOTATION MARK ORNAMENT]
        case 0x275c: // ❜	[HEAVY SINGLE COMMA QUOTATION MARK ORNAMENT]
        case 0xff07: // ＇	[FULLWIDTH APOSTROPHE]
            outString += "'";
            break;
        case 0x2010: // ‐	[HYPHEN]
        case 0x2011: // ‑	[NON-BREAKING HYPHEN]
        case 0x2012: // ‒	[FIGURE DASH]
        case 0x2013: // –	[EN DASH]
        case 0x2014: // —	[EM DASH]
        case 0x207b: // ⁻	[SUPERSCRIPT MINUS]
        case 0x208b: // ₋	[SUBSCRIPT MINUS]
        case 0xff0d: // －	[FULLWIDTH HYPHEN-MINUS]
            outString += '-';
            break;
        case 0x2045: // ⁅	[LEFT SQUARE BRACKET WITH QUILL]
        case 0x2772: // ❲	[LIGHT LEFT TORTOISE SHELL BRACKET ORNAMENT]
        case 0xff3b: // ［	[FULLWIDTH LEFT SQUARE BRACKET]
            outString += '[';
            break;
        case 0x2046: // ⁆	[RIGHT SQUARE BRACKET WITH QUILL]
        case 0x2773: // ❳	[LIGHT RIGHT TORTOISE SHELL BRACKET ORNAMENT]
        case 0xff3d: // ］	[FULLWIDTH RIGHT SQUARE BRACKET]
            outString += ']';
            break;
        case 0x207d: // ⁽	[SUPERSCRIPT LEFT PARENTHESIS]
        case 0x208d: // ₍	[SUBSCRIPT LEFT PARENTHESIS]
        case 0x2768: // ❨	[MEDIUM LEFT PARENTHESIS ORNAMENT]
        case 0x276a: // ❪	[MEDIUM FLATTENED LEFT PARENTHESIS ORNAMENT]
        case 0xff08: // （	[FULLWIDTH LEFT PARENTHESIS]
            outString += '(';
            break;
        case 0x2e28: // ⸨	[LEFT DOUBLE PARENTHESIS]
            outString += '(';
            outString += '(';
            break;
        case 0x207e: // ⁾	[SUPERSCRIPT RIGHT PARENTHESIS]
        case 0x208e: // ₎	[SUBSCRIPT RIGHT PARENTHESIS]
        case 0x2769: // ❩	[MEDIUM RIGHT PARENTHESIS ORNAMENT]
        case 0x276b: // ❫	[MEDIUM FLATTENED RIGHT PARENTHESIS ORNAMENT]
        case 0xff09: // ）	[FULLWIDTH RIGHT PARENTHESIS]
            outString += ')';
            break;
        case 0x2e29: // ⸩	[RIGHT DOUBLE PARENTHESIS]
            outString += ')';
            outString += ')';
            break;
        case 0x276c: // ❬	[MEDIUM LEFT-POINTING ANGLE BRACKET ORNAMENT]
        case 0x2770: // ❰	[HEAVY LEFT-POINTING ANGLE BRACKET ORNAMENT]
        case 0xff1c: // ＜	[FULLWIDTH LESS-THAN SIGN]
            outString += '<';
            break;
        case 0x276d: // ❭	[MEDIUM RIGHT-POINTING ANGLE BRACKET ORNAMENT]
        case 0x2771: // ❱	[HEAVY RIGHT-POINTING ANGLE BRACKET ORNAMENT]
        case 0xff1e: // ＞	[FULLWIDTH GREATER-THAN SIGN]
            outString += '>';
            break;
        case 0x2774: // ❴	[MEDIUM LEFT CURLY BRACKET ORNAMENT]
        case 0xff5b: // ｛	[FULLWIDTH LEFT CURLY BRACKET]
            outString += '{';
            break;
        case 0x2775: // ❵	[MEDIUM RIGHT CURLY BRACKET ORNAMENT]
        case 0xff5d: // ｝	[FULLWIDTH RIGHT CURLY BRACKET]
            outString += '}';
            break;
        case 0x207a: // ⁺	[SUPERSCRIPT PLUS SIGN]
        case 0x208a: // ₊	[SUBSCRIPT PLUS SIGN]
        case 0xff0b: // ＋	[FULLWIDTH PLUS SIGN]
            outString += '+';
            break;
        case 0x207c: // ⁼	[SUPERSCRIPT EQUALS SIGN]
        case 0x208c: // ₌	[SUBSCRIPT EQUALS SIGN]
        case 0xff1d: // ＝	[FULLWIDTH EQUALS SIGN]
            outString += '=';
            break;
        case 0xff01: // ！	[FULLWIDTH EXCLAMATION MARK]
            outString += '!';
            break;
        case 0x203c: // ‼	[DOUBLE EXCLAMATION MARK]
            outString += '!';
            outString += '!';
            break;
        case 0x2049: // ⁉	[EXCLAMATION QUESTION MARK]
            outString += '!';
            outString += '?';
            break;
        case 0xff03: // ＃	[FULLWIDTH NUMBER SIGN]
            outString += '#';
            break;
        case 0xff04: // ＄	[FULLWIDTH DOLLAR SIGN]
            outString += '$';
            break;
        case 0x2052: // ⁒	[COMMERCIAL MINUS SIGN]
        case 0xff05: // ％	[FULLWIDTH PERCENT SIGN]
            outString += '%';
            break;
        case 0xff06: // ＆	[FULLWIDTH AMPERSAND]
            outString += '&';
            break;
        case 0x204e: // ⁎	[LOW ASTERISK]
        case 0xff0a: // ＊	[FULLWIDTH ASTERISK]
            outString += '*';
            break;
        case 0xff0c: // ，	[FULLWIDTH COMMA]
            outString += ',';
            break;
        case 0xff0e: // ．	[FULLWIDTH FULL STOP]
            outString += '.';
            break;
        case 0x2044: // ⁄	[FRACTION SLASH]
        case 0xff0f: // ／	[FULLWIDTH SOLIDUS]
            outString += '/';
            break;
        case 0xff1a: // ：	[FULLWIDTH COLON]
            outString += ':';
            break;
        case 0x204f: // ⁏	[REVERSED SEMICOLON]
        case 0xff1b: // ；	[FULLWIDTH SEMICOLON]
            outString += ';';
            break;
        case 0xff1f: // ？	[FULLWIDTH QUESTION MARK]
            outString += '?';
            break;
        case 0x2047: // ⁇	[DOUBLE QUESTION MARK]
            outString += '?';
            outString += '?';
            break;
        case 0x2048: // ⁈	[QUESTION EXCLAMATION MARK]
            outString += '?';
            outString += '!';
            break;
        case 0xff20: // ＠	[FULLWIDTH COMMERCIAL AT]
            outString += '@';
            break;
        case 0xff3c: // ＼	[FULLWIDTH REVERSE SOLIDUS]
            outString += '\\';
            break;
        case 0x2038: // ‸	[CARET]
        case 0xff3e: // ＾	[FULLWIDTH CIRCUMFLEX ACCENT]
            outString += '^';
            break;
        case 0xff3f: // ＿	[FULLWIDTH LOW LINE]
            outString += '_';
            break;
        case 0x2053: // ⁓	[SWUNG DASH]
        case 0xff5e: // ～	[FULLWIDTH TILDE]
            outString += '~';
            break;
        default:
            outString += replaceUnmapped ? defaultString : String.fromCharCode(charCode);
            break;
    }

    return outString;
};

const foldToASCII = (inStr: string | null | undefined): string => {
    if (!inStr) {
        return '';
    }

    /*
     * The array of characters or character combinations to output
     */
    let outStr = '';

    for (let i = 0; i < inStr.length; i++) {
        const charCode = fixedCharCodeAt(inStr, i);

        /*
         * Skip low surrogates
         */
        if (charCode) {
            if (charCode < 128) {
                /*
                 * Character within the ASCII range.
                 * Copy it to the output string.
                 */
                outStr += String.fromCharCode(charCode);
            } else {
                /*
                 * Character outside of the ASCII range.
                 * Look for a replacement
                 */
                outStr += replaceChar(charCode);
            }
        }
    }

    return outStr;
};

export default foldToASCII;
